import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { notificar } from '../utils/notify';
import { buscarCredencial } from '../utils/credenciaisBpoRh';
import { useTempoAtivo, guardarTempoAtivoLS, limparTempoAtivoLS } from '../components/TempoAtivoContext';

// Painel de ATIVIDADES MENSAIS do BPO (substitui o painel da PlayBPO).
// Visão 1: empresas com contagem (total / vencidas / a vencer).
// Visão 2: tarefas da empresa — concluir, cronômetro, descrição, CRUD.
// A rota define a área: /BpoTarefas/RH ou /BpoTarefas/FIN.

const CORES_STATUS = {
  'A fazer': { classe: 'bg-secondary', icone: 'fa-circle' },
  'Atrasada': { classe: 'bg-danger', icone: 'fa-triangle-exclamation' },
  'Finalizada': { classe: 'bg-success', icone: 'fa-circle-check' },
  'Finalizada em atraso': { classe: 'bg-warning text-dark', icone: 'fa-clock' },
};

const ROTULO_AREA = { RH: 'BPO RH', FIN: 'BPO Financeiro' };
const FORM_VAZIO = {
  id: null, data: '', empresa: '', tarefa: '', descricao: '', responsavel: '',
  status: 'A fazer', recorrente: false,
  periodicidade: 'Mensal', diasSemana: [], dia: '', mesAnual: '',
};

// Periodicidades da recorrência (mesmos valores do backend)
const PERIODICIDADES = ['Diária', 'Semanal', 'Quinzenal', 'Mensal', 'Anual'];
// weekday() do Python: 0=Segunda ... 6=Domingo
const DIAS_SEMANA = [
  [0, 'Seg'], [1, 'Ter'], [2, 'Qua'], [3, 'Qui'], [4, 'Sex'], [5, 'Sáb'], [6, 'Dom'],
];
const MESES_ANO = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const mesAtual = () => new Date().toISOString().slice(0, 7);
const mesSeguinte = () => {
  const d = new Date(); d.setMonth(d.getMonth() + 1);
  return d.toISOString().slice(0, 7);
};

// 'HH:MM:SS' -> segundos (tolerante a nulo/lixo)
const seg = (hms) => {
  if (!hms) return 0;
  const p = String(hms).split(':').map(Number);
  return p.length === 3 && p.every(n => !Number.isNaN(n)) ? p[0] * 3600 + p[1] * 60 + p[2] : 0;
};
// 0,38% não pode virar '0%' nem 99,6% virar '100%' — mostra 1 casa nas bordas
const pctTexto = (n) => {
  if (n > 0 && n < 1) return n.toFixed(1).replace('.', ',');
  if (n > 99 && n < 100) return n.toFixed(1).replace('.', ',');
  return String(Math.round(n));
};

const hms = (s) =>
  `${String(Math.floor(s / 3600)).padStart(2, '0')}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

const BpoTarefasMensais = () => {
  const { area: areaParam } = useParams();
  const area = (areaParam || 'RH').toUpperCase() === 'FIN' ? 'FIN' : 'RH';
  // widget flutuante global: avisamos o contexto a cada iniciar/parar para a
  // pill do canto refletir na hora (a fonte de verdade continua no servidor)
  const { sincronizar: sincronizarTempoAtivo } = useTempoAtivo();

  const [tarefas, setTarefas] = useState([]);
  const [empresasOpcoes, setEmpresasOpcoes] = useState([]);
  const [empresasInativas, setEmpresasInativas] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);
  const [meses, setMeses] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [mensagem, setMensagem] = useState('');
  useEffect(() => { notificar(mensagem); }, [mensagem]);

  // Navegação e filtros. A empresa aberta vive na URL (?empresa=) para o
  // botão VOLTAR do navegador funcionar: sem isso a troca de visão não gera
  // histórico e o voltar pulava para a última rota visitada.
  const [searchParams, setSearchParams] = useSearchParams();
  const empresaSelecionada = searchParams.get('empresa');
  const setEmpresaSelecionada = (nome) =>
    setSearchParams(nome ? { empresa: nome } : {}, nome ? undefined : { replace: false });
  const [mes, setMes] = useState(mesAtual());
  const [filtroResponsavel, setFiltroResponsavel] = useState('');
  const [filtroEmpresa, setFiltroEmpresa] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [busca, setBusca] = useState('');

  // Formulário / exclusão / detalhe / cronômetro
  const [form, setForm] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [excluir, setExcluir] = useState(null);
  const [detalhe, setDetalhe] = useState(null);           // tarefa aberta (descrição)
  const [descricaoEdit, setDescricaoEdit] = useState('');
  const [acaoId, setAcaoId] = useState(null);
  const [agora, setAgora] = useState(Date.now());          // tique do cronômetro
  const [buscadoEm, setBuscadoEm] = useState(Date.now());  // quando a lista chegou

  const areaAnterior = React.useRef(area);
  useEffect(() => {
    if (areaAnterior.current !== area) {
      areaAnterior.current = area;
      setSearchParams({}, { replace: true });   // limpa ?empresa= sem empilhar histórico
    }
    setFiltroResponsavel(''); setFiltroStatus('');
    setFiltroEmpresa(''); setBusca(''); setForm(null); setDetalhe(null);
  }, [area]); // eslint-disable-line react-hooks/exhaustive-deps

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
  const API = `${process.env.REACT_APP_API_BASE}/api/bpo/tarefas/`;

  const carregar = useCallback((silencioso = false) => {
    if (!silencioso) setCarregando(true);
    axios
      .get(API, {
        params: { area, mes: mes || undefined, busca: busca || undefined },
        headers: headers(),
      })
      .then((res) => {
        setTarefas(res.data.tarefas || []);
        setEmpresasOpcoes(res.data.empresas || []);
        setEmpresasInativas(res.data.empresas_inativas || []);
        setResponsaveis(res.data.responsaveis || []);
        setMeses(res.data.meses || []);
        setBuscadoEm(Date.now());
      })
      .catch((error) => {
        if (!silencioso) setMensagem(`${error.response?.data?.erro || 'Erro ao carregar as tarefas'}`);
      })
      .finally(() => setCarregando(false));
  }, [area, mes, busca]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { carregar(); }, [carregar]);

  // Visão 1: filtros de empresa, operadora e status alimentam totais, dash e tabela
  const tarefasV1 = useMemo(() => tarefas.filter(t => {
    if (filtroEmpresa && t.empresa !== filtroEmpresa) return false;
    if (filtroResponsavel && t.responsavel !== filtroResponsavel) return false;
    if (filtroStatus && t.status !== filtroStatus) return false;
    return true;
  }), [tarefas, filtroEmpresa, filtroResponsavel, filtroStatus]);

  // ─── agregação por empresa (visão 1) ───
  const empresas = useMemo(() => {
    const grupos = {};
    tarefasV1.forEach(t => {
      const g = grupos[t.empresa] = grupos[t.empresa] || {
        empresa: t.empresa, total: 0, vencidas: 0, aVencer: 0, concluidas: 0, tempoSeg: 0, tempoN: 0,
      };
      g.total += 1;
      if (t.status === 'Atrasada') g.vencidas += 1;
      else if (t.status === 'A fazer') g.aVencer += 1;
      else g.concluidas += 1;
      if (t.tempo_execucao && seg(t.tempo_execucao) > 0) {
        g.tempoSeg += seg(t.tempo_execucao); g.tempoN += 1;
      }
    });
    return Object.values(grupos).sort((a, b) => a.empresa.localeCompare(b.empresa, 'pt-BR'));
  }, [tarefasV1]);

  // todas as empresas da área (sem os filtros da tela) — para o atalho 'Incluir horas'
  const todasEmpresas = useMemo(
    () => [...new Set(tarefas.map(t => t.empresa).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [tarefas]);

  const totais = useMemo(() => ({
    total: tarefasV1.length,
    vencidas: tarefasV1.filter(t => t.status === 'Atrasada').length,
    aVencer: tarefasV1.filter(t => t.status === 'A fazer').length,
    concluidas: tarefasV1.filter(t => t.status === 'Finalizada' || t.status === 'Finalizada em atraso').length,
  }), [tarefasV1]);

  // ─── dados do dash (mesmo desenho da página Contabilidade) ───
  const distStatus = useMemo(() =>
    Object.keys(CORES_STATUS)
      .map(nome => ({ name: nome, value: tarefasV1.filter(t => t.status === nome).length }))
      .filter(d => d.value > 0),
    [tarefasV1]);

  // Paleta Comece (theme.css): navy #00205b · cyan #00b5cc · gold #f1be48 ·
  // verde FMP #1fa971. Vermelho segue só na Atrasada — é o sinal de alerta.
  const CORES_PIZZA = {
    'A fazer': '#00b5cc', 'Atrasada': '#dc3545',
    'Finalizada': '#1fa971', 'Finalizada em atraso': '#f1be48',
  };

  const prazoBuckets = useMemo(() => {
    const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
    const iso = (d) => d.toISOString().slice(0, 10);
    const em = (dias) => { const d = new Date(hoje); d.setDate(d.getDate() + dias); return iso(d); };
    const pend = tarefasV1.filter(t => t.status === 'A fazer' || t.status === 'Atrasada');
    return [
      { name: 'Atrasadas', color: '#dc3545', value: pend.filter(t => t.status === 'Atrasada').length },
      { name: 'Hoje', color: '#f1be48', value: pend.filter(t => t.status === 'A fazer' && t.data_iso === iso(hoje)).length },
      { name: '1–7 dias', color: '#00b5cc', value: pend.filter(t => t.status === 'A fazer' && t.data_iso > iso(hoje) && t.data_iso <= em(7)).length },
      { name: '8–15 dias', color: '#00205b', value: pend.filter(t => t.status === 'A fazer' && t.data_iso > em(7) && t.data_iso <= em(15)).length },
      { name: '+15 dias', color: '#1fa971', value: pend.filter(t => t.status === 'A fazer' && t.data_iso > em(15)).length },
    ];
  }, [tarefasV1]);

  // ─── tarefas da empresa aberta (visão 2) ───
  // Visão por DIA (pedido de 13/10/2026): abre em hoje — as tarefas do dia e, no dia
  // de hoje, as atrasadas de dias anteriores ainda abertas. "Todas" mostra a lista
  // inteira, com as de hoje primeiro. Data local (não UTC: depois das 21h o UTC já é amanhã).
  const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const hojeIso = isoLocal(new Date());
  const [diaVer, setDiaVer] = useState(hojeIso);        // '' = todas
  const moverDia = (n) => setDiaVer((d) => {
    const [a, m, dd] = (d || hojeIso).split('-').map(Number);
    return isoLocal(new Date(a, m - 1, dd + n));
  });
  useEffect(() => { setDiaVer(isoLocal(new Date())); }, [empresaSelecionada]); // eslint-disable-line react-hooks/exhaustive-deps
  const tarefasEmpresa = useMemo(() => {
    if (!empresaSelecionada) return [];
    const fechada = (t) => String(t.status || '').startsWith('Finalizada');
    const lista = tarefas.filter(t => {
      if (t.empresa !== empresaSelecionada) return false;
      if (filtroResponsavel && t.responsavel !== filtroResponsavel) return false;
      if (filtroStatus && t.status !== filtroStatus) return false;
      if (diaVer) {
        if (t.data_iso === diaVer) return true;
        // no dia de hoje, o que ficou para trás e não foi feito também aparece
        return diaVer === hojeIso && !fechada(t) && t.data_iso && t.data_iso < hojeIso;
      }
      return true;
    });
    // ordem: hoje (abertas) → atrasadas → próximas por data → concluídas
    const peso = (t) => (fechada(t) ? 3 : t.data_iso === hojeIso ? 0 : (t.data_iso || '') < hojeIso ? 1 : 2);
    return lista.sort((a, b) => (peso(a) - peso(b)) || String(a.data_iso || '').localeCompare(String(b.data_iso || '')));
  }, [tarefas, empresaSelecionada, filtroResponsavel, filtroStatus, diaVer, hojeIso]);

  // segundos exibidos: acumulado + decorrido. O decorrido vem do BANCO
  // (cronometro_seg, na hora da busca) e o navegador só soma o tempo desde
  // a busca — relógio do servidor (UTC) e do navegador nunca são comparados.
  const tempoAtual = (t) => {
    let s = seg(t.tempo_execucao);
    if (t.iniciado_em) {
      s += Math.max(t.cronometro_seg || 0, 0)
        + Math.max(Math.floor((agora - buscadoEm) / 1000), 0);
    }
    return s;
  };

  // ─── tempo GERAL no cliente: conta enquanto a empresa está aberta ───
  // Abrir a empresa inicia; iniciar uma tarefa pausa (o relógio passa a ser o
  // da tarefa); parar/concluir a tarefa retoma. Só um relógio por usuário.
  const API_TEMPO = `${process.env.REACT_APP_API_BASE}/api/bpo/tempo-cliente/`;
  const [tempoCliente, setTempoCliente] = useState(null); // {segundos, rodando, cronometro_seg, buscadoEm}

  // tique de 1s enquanto houver relógio rodando — cronômetro de tarefa OU o
  // tempo geral do cliente (senão o badge congela enquanto o widget conta)
  const temCronometro = tarefas.some(t => t.iniciado_em);
  const temRelogio = temCronometro || Boolean(tempoCliente?.rodando);
  useEffect(() => {
    if (!temRelogio) return;
    const t = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(t);
  }, [temRelogio]);

  const buscarTempoCliente = useCallback((empresa) => {
    if (!empresa) { setTempoCliente(null); return; }
    axios.get(API_TEMPO, { params: { area, empresa }, headers: headers() })
      .then((res) => setTempoCliente({ ...res.data, buscadoEm: Date.now() }))
      .catch(() => setTempoCliente(null));
  }, [area]); // eslint-disable-line react-hooks/exhaustive-deps

  const acionarTempoCliente = useCallback((acao, empresa) => {
    // registro da retomada pós-F5 (TempoAtivoContext): iniciar guarda, parar
    // limpa — assim o F5 religa a empresa certa e a parada explícita não religa
    if (acao === 'iniciar' && empresa) guardarTempoAtivoLS([{ area, empresa }]);
    else if (acao === 'parar') limparTempoAtivoLS();
    return axios.post(API_TEMPO, { area, empresa: empresa || '', acao }, { headers: headers() })
      .catch(() => {});   // tempo geral nunca pode travar o fluxo das tarefas
  }, [area]); // eslint-disable-line react-hooks/exhaustive-deps

  // alguma tarefa DESTA empresa com cronômetro rodando? então o relógio é dela
  const tarefaRodando = React.useMemo(() =>
    Boolean(empresaSelecionada && tarefas.some(t =>
      t.empresa === empresaSelecionada && t.iniciado_em)),
    [tarefas, empresaSelecionada]);

  // Abrir a empresa liga o relógio. NAVEGAR PARA OUTRA PÁGINA NÃO PARA — ele
  // segue correndo no widget flutuante (TempoFlutuante), e fechar a aba /
  // deslogar é tratado pelo TempoAtivoContext (pagehide com keepalive).
  // Voltar para a lista NÃO para o relógio: quem consulta a carteira e volta
  // para a empresa continua trabalhando nela. Parar é ação explícita — o botão
  // do widget flutuante, iniciar uma tarefa ou abrir outra empresa (aí o
  // backend fecha a sessão anterior sozinho).
  useEffect(() => {
    if (!empresaSelecionada) {
      // segue contando com a empresa que ficou aberta; o widget mostra qual é
      // e é por lá que se para
      return;
    }
    if (!tarefaRodando) {
      acionarTempoCliente('iniciar', empresaSelecionada)
        .then(() => { buscarTempoCliente(empresaSelecionada); sincronizarTempoAtivo(); });
    } else {
      buscarTempoCliente(empresaSelecionada);
    }
  }, [empresaSelecionada, tarefaRodando]); // eslint-disable-line react-hooks/exhaustive-deps

  const tempoClienteAtual = () => {
    if (!tempoCliente) return 0;
    let s = tempoCliente.segundos || 0;
    if (tempoCliente.rodando) {
      s += Math.max(tempoCliente.cronometro_seg || 0, 0)
        + Math.max(Math.floor((agora - tempoCliente.buscadoEm) / 1000), 0);
    }
    return s;
  };

  const alternarCronometro = (t) => {
    setAcaoId(t.id);
    const corpo = t.iniciado_em ? { parar: true } : { iniciar: true };
    // iniciar tarefa pausa o relógio do cliente; parar tarefa devolve pra ele
    const antes = corpo.iniciar
      ? acionarTempoCliente('parar')
      : Promise.resolve();
    antes
      .then(() => axios.patch(`${API}${t.id}/`, corpo, { headers: headers() }))
      .then((res) => {
        if (corpo.parar) setMensagem(`${res.data.mensagem}`);
        sincronizarTempoAtivo();
        if (corpo.parar && empresaSelecionada) {
          return acionarTempoCliente('iniciar', empresaSelecionada)
            .then(() => buscarTempoCliente(empresaSelecionada))
            .then(() => { sincronizarTempoAtivo(); carregar(true); });
        }
        buscarTempoCliente(empresaSelecionada);
        carregar(true);
      })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Falha no cronômetro'}`))
      .finally(() => setAcaoId(null));
  };

  const concluirTarefa = (t) => {
    setAcaoId(t.id);
    axios
      .patch(`${API}${t.id}/`, { concluir: true }, { headers: headers() })
      .then((res) => {
        setMensagem(`${res.data.mensagem}`);
        sincronizarTempoAtivo();
        // se o cronômetro da tarefa estava rodando, o relógio volta ao cliente
        if (t.iniciado_em && empresaSelecionada) {
          return acionarTempoCliente('iniciar', empresaSelecionada)
            .then(() => buscarTempoCliente(empresaSelecionada))
            .then(() => { sincronizarTempoAtivo(); carregar(true); });
        }
        carregar(true);
      })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Não foi possível concluir'}`))
      .finally(() => setAcaoId(null));
  };

  const reabrirTarefa = (t) => {
    setAcaoId(t.id);
    axios
      .patch(`${API}${t.id}/`, { reabrir: true }, { headers: headers() })
      .then(() => { setMensagem('Tarefa reaberta'); carregar(true); })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Não foi possível reabrir'}`))
      .finally(() => setAcaoId(null));
  };

  const abrirDetalhe = (t) => { setDetalhe(t); setDescricaoEdit(t.descricao || ''); };

  const salvarDescricao = () => {
    if (!detalhe) return;
    setSalvando(true);
    axios
      .patch(`${API}${detalhe.id}/`, { descricao: descricaoEdit }, { headers: headers() })
      .then(() => { setMensagem('Descrição salva'); setDetalhe(null); carregar(true); })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Não foi possível salvar'}`))
      .finally(() => setSalvando(false));
  };

  const abrirNova = () => setForm({
    ...FORM_VAZIO,
    data: new Date().toISOString().slice(0, 10),
    empresa: empresaSelecionada || '',
    responsavel: operadoraPadrao || '',
  });
  const abrirEdicao = (t) => setForm({
    id: t.id, data: t.data_iso, empresa: t.empresa, tarefa: t.tarefa,
    descricao: t.descricao || '', responsavel: t.responsavel || '', status: t.status,
  });
  const setF = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  const salvar = () => {
    if (!form.empresa.trim() || !form.tarefa.trim() || !form.data) {
      setMensagem('Data, empresa e tarefa são obrigatórias');
      return;
    }
    setSalvando(true);
    const payload = {
      area, data: form.data, empresa: form.empresa.trim(), tarefa: form.tarefa.trim(),
      descricao: form.descricao.trim(), responsavel: form.responsavel.trim(),
      recorrente: !form.id && form.recorrente,
    };
    if (payload.recorrente) {
      payload.periodicidade = form.periodicidade;
      if (form.periodicidade === 'Semanal') {
        if (form.diasSemana.length === 0) {
          setMensagem('Recorrência semanal: marque ao menos um dia da semana');
          setSalvando(false);
          return;
        }
        payload.dias_semana = form.diasSemana.join(',');
      }
      if (form.periodicidade === 'Quinzenal' || form.periodicidade === 'Mensal') {
        payload.dia = form.dia || undefined;
      }
      if (form.periodicidade === 'Anual') {
        payload.dia = form.dia || undefined;
        payload.mes = form.mesAnual || undefined;
      }
    }
    const req = form.id
      ? axios.patch(`${API}${form.id}/`, { ...payload, status: form.status }, { headers: headers() })
      : axios.post(API, payload, { headers: headers() });
    req
      .then((res) => { setMensagem(`${res.data.mensagem}`); setForm(null); carregar(true); })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Não foi possível salvar'}`))
      .finally(() => setSalvando(false));
  };

  const [pararRecorrencia, setPararRecorrencia] = useState(false);
  useEffect(() => { setPararRecorrencia(false); }, [excluir]);

  // ─── Editar empresa (renomear / trocar operadora em tudo) ───
  const [empresaEdit, setEmpresaEdit] = useState(null);   // {original, nome, operadora, dados}

  // ─── Dados cadastrais da empresa (razão social, CNPJ, regime...) ───
  const REGIMES = ['Simples Nacional', 'Lucro Presumido', 'Lucro Real', 'MEI', 'Imune / Isenta'];
  const DADOS_VAZIO = {
    razao_social: '', nome_fantasia: '', cnpj: '', regime_tributario: '',
    inscricao_estadual: '', inscricao_municipal: '', endereco: '', observacao: '',
  };
  const [dadosVer, setDadosVer] = useState(null);   // {empresa, dados, carregando}

  const abrirDadosEmpresa = (empresa) => {
    setDadosVer({ empresa, dados: null, carregando: true });
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bpo/empresas/dados/`,
        { params: { area, empresa }, headers: headers() })
      .then((res) => setDadosVer({ empresa, dados: res.data.dados, carregando: false }))
      .catch(() => setDadosVer({ empresa, dados: null, carregando: false }));
  };

  // abre o modal de edição já carregando os dados cadastrais
  const abrirEdicaoEmpresa = (empresa) => {
    // pré-seleciona a operadora das tarefas PENDENTES (é o que a edição altera);
    // as concluídas guardam o responsável antigo e faziam o select cair em
    // "Não alterar" sempre que a operadora já tinha sido trocada uma vez
    const daEmpresa = tarefas.filter(t => t.empresa === empresa && t.responsavel);
    const pendentes = daEmpresa.filter(t => !t.data_finalizada);
    const conta = {};
    (pendentes.length ? pendentes : daEmpresa)
      .forEach(t => { conta[t.responsavel] = (conta[t.responsavel] || 0) + 1; });
    const operadoraAtual = Object.keys(conta)
      .sort((a, b) => conta[b] - conta[a])[0] || '';
    setEmpresaEdit({
      original: empresa, nome: empresa,
      operadora: operadoraAtual,
      dados: { ...DADOS_VAZIO }, carregandoDados: true,
    });
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bpo/empresas/dados/`,
        { params: { area, empresa }, headers: headers() })
      .then((res) => {
        const d = res.data.dados || {};
        const limpo = {}; Object.keys(DADOS_VAZIO).forEach(k => { limpo[k] = d[k] || ''; });
        setEmpresaEdit(f => (f && f.original === empresa
          ? { ...f, dados: limpo, carregandoDados: false } : f));
      })
      .catch(() => setEmpresaEdit(f => (f ? { ...f, carregandoDados: false } : f)));
  };

  const salvarEmpresa = () => {
    if (!empresaEdit?.nome?.trim()) {
      setMensagem('O nome da empresa não pode ficar vazio');
      return;
    }
    setSalvando(true);
    const novoNome = empresaEdit.nome.trim();
    const renomeou = novoNome !== empresaEdit.original;
    const mudouCabecalho = renomeou || !!empresaEdit.operadora;
    const base = `${process.env.REACT_APP_API_BASE}/api/bpo/empresas`;
    const empresaFinal = renomeou ? novoNome : empresaEdit.original;

    // 1) nome/operadora (só se mudou) → 2) dados cadastrais (sempre, no nome final)
    const passoCabecalho = mudouCabecalho
      ? axios.patch(`${base}/editar/`, {
        area, empresa: empresaEdit.original,
        novo_nome: renomeou ? novoNome : '',
        responsavel: empresaEdit.operadora || '',
      }, { headers: headers() })
      : Promise.resolve(null);

    passoCabecalho
      .then(() => axios.patch(`${base}/dados/`,
        { area, empresa: empresaFinal, ...(empresaEdit.dados || {}) },
        { headers: headers() }))
      .then(() => {
        setMensagem('Empresa atualizada');
        setEmpresaEdit(null);
        carregar(true);
      })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Não foi possível salvar a empresa'}`))
      .finally(() => setSalvando(false));
  };

  const alternarAtivacao = (empresa, ativo) => {
    setSalvando(true);
    axios
      .patch(`${process.env.REACT_APP_API_BASE}/api/bpo/empresas/ativar/`,
        { area, empresa, ativo }, { headers: headers() })
      .then((res) => {
        setMensagem(`${res.data.mensagem}`);
        setEmpresaEdit(null);
        carregar(true);
      })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Não foi possível atualizar'}`))
      .finally(() => setSalvando(false));
  };

  // ─── Histórico de tempo por empresa (todas as competências) ───
  const [histEmpresa, setHistEmpresa] = useState(null);   // null | nome da empresa
  const [histTarefas, setHistTarefas] = useState([]);
  const [histCliente, setHistCliente] = useState([]);     // tempo geral no cliente, por dia
  const [carregandoHist, setCarregandoHist] = useState(false);
  // 'Incluir horas' esquecidas (13/10/2026): soma no tempo fora de tarefa do dia ou numa tarefa
  const [histTodas, setHistTodas] = useState([]);          // todas as tarefas da empresa
  const [incluirHoras, setIncluirHoras] = useState(null);  // null | {dia, horas, tarefa_id, motivo}

  const abrirHistorico = (empresa) => {
    setHistEmpresa(empresa);
    setHistEdit(null);
    setIncluirHoras(null);
    setCarregandoHist(true);
    Promise.all([
      axios.get(API, { params: { area, empresa }, headers: headers() })   // sem mes = todo o período
        .then((res) => {
          const todas = (res.data.tarefas || []).slice().sort((a, b) => b.data_iso.localeCompare(a.data_iso));
          setHistTodas(todas);
          return todas.filter(t => seg(t.tempo_execucao) > 0);
        })
        .catch(() => []),
      // tempo geral no cliente (fora de tarefas), por dia/operadora
      axios.get(API_TEMPO, { params: { area, empresa, historico: 1 }, headers: headers() })
        .then((res) => res.data.dias || [])
        .catch(() => []),
    ]).then(([tarefasHist, diasCliente]) => {
      setHistTarefas(tarefasHist);
      setHistCliente(diasCliente);
    }).finally(() => setCarregandoHist(false));
  };

  // edição inline do tempo no histórico: {id, valor}
  const [histEdit, setHistEdit] = useState(null);

  const hojeLocal = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  // como o campo HH:MM está sendo lido — mostrado ao lado, para '25:00' não virar
  // 25 horas sem a operadora perceber; erro aparece no próprio formulário (o aviso
  // geral fica atrás do modal)
  const leituraHoras = (txt) => {
    const m = /^(\d{1,2}):(\d{2})$/.exec((txt || '').trim());
    if (!m) return { ok: false, texto: (txt || '').trim() ? 'use HH:MM — ex.: 00:25 para 25 minutos' : '' };
    const h = Number(m[1]); const mi = Number(m[2]);
    if (mi > 59) return { ok: false, texto: 'os minutos vão até 59' };
    const total = h * 60 + mi;
    const rot = [h ? `${h} h` : '', mi ? `${mi} min` : ''].filter(Boolean).join(' ') || '0 min';
    if (total < 1) return { ok: false, texto: 'informe pelo menos 1 minuto' };
    if (total > 12 * 60) return { ok: false, texto: `= ${rot}: o limite é 12 h por inclusão` };
    return { ok: true, texto: `= ${rot}` };
  };
  const salvarInclusaoHoras = () => {
    if (!incluirHoras) return;
    const lido = leituraHoras(incluirHoras.horas);
    if (!lido.ok) {
      setIncluirHoras(f => ({ ...f, erro: lido.texto || 'Informe as horas no formato HH:MM' }));
      return;
    }
    setSalvando(true);
    axios.post(API_TEMPO, {
      acao: 'incluir', area, empresa: histEmpresa, dia: incluirHoras.dia, horas: incluirHoras.horas.trim(),
      tarefa_id: incluirHoras.tarefa_id || null, motivo: incluirHoras.motivo,
    }, { headers: headers() })
      .then((res) => {
        setMensagem(res.data.mensagem || 'Horas incluídas');
        setIncluirHoras(null);
        abrirHistorico(histEmpresa);
        carregar(true);
      })
      .catch((error) => setIncluirHoras(f => f && ({
        ...f, erro: error.response?.data?.mensagem || 'Não foi possível incluir as horas' })))
      .finally(() => setSalvando(false));
  };

  // atalho da tela inicial (20/10/2026): 'Incluir horas' sem procurar o histórico da
  // empresa — escolhe a empresa e abre o histórico já com o formulário aberto
  const [horasEmpresa, setHorasEmpresa] = useState(null);   // null | nome escolhido ('' = nenhum)
  const abrirIncluirHoras = (empresa) => {
    setHorasEmpresa(null);
    abrirHistorico(empresa);
    setIncluirHoras({ dia: hojeLocal(), horas: '', tarefa_id: '', motivo: '' });
  };

  const salvarTempo = () => {
    if (!histEdit) return;
    setSalvando(true);
    axios
      .patch(`${API}${histEdit.id}/`, { tempo_execucao: histEdit.valor.trim() }, { headers: headers() })
      .then(() => {
        setMensagem('Tempo atualizado');
        // reflete no modal sem refazer a busca inteira
        setHistTarefas(lista => lista
          .map(t => t.id === histEdit.id ? { ...t, tempo_execucao: histEdit.valor.trim() || null } : t)
          .filter(t => seg(t.tempo_execucao) > 0));
        setHistEdit(null);
        carregar(true);   // tempo médio da tabela de empresas acompanha
      })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Não foi possível salvar o tempo'}`))
      .finally(() => setSalvando(false));
  };

  // agrupado por competência, com subtotal — tarefas + tempo geral no cliente
  const histPorMes = useMemo(() => {
    const itensCliente = histCliente.map(d => ({
      tipo: 'cliente',
      id: `cli-${d.dia}-${d.usuario}`,
      data_iso: d.dia,
      data: d.dia.split('-').reverse().join('/'),
      tarefa: 'Tempo no cliente (fora de tarefas)',
      tempo_execucao: hms(d.segundos),
      executor: d.usuario,
    }));
    const grupos = {};
    [...histTarefas, ...itensCliente].forEach(t => {
      const m = t.data_iso.slice(0, 7);
      (grupos[m] = grupos[m] || { mes: m, itens: [], totalSeg: 0 });
      grupos[m].itens.push(t);
      grupos[m].totalSeg += seg(t.tempo_execucao);
    });
    Object.values(grupos).forEach(g =>
      g.itens.sort((a, b) => b.data_iso.localeCompare(a.data_iso)));
    return Object.values(grupos).sort((a, b) => b.mes.localeCompare(a.mes));
  }, [histTarefas, histCliente]);

  const histTotalSeg = histTarefas.reduce((soma, t) => soma + seg(t.tempo_execucao), 0)
    + histCliente.reduce((soma, d) => soma + (d.segundos || 0), 0);

  // ─── Acessos da empresa (equivalente 'Senhas salvas' PlayBPO) ───
  const TIPOS_ACESSO = ['Banco', 'Drive / Pasta', 'ERP / Sistema', 'Portal', 'E-mail', 'Certificado', 'Outro'];
  const ACESSO_VAZIO = {
    id: null, nome: '', tipo: '', url: '', usuario: '', senha: '',
    operador: '', codigo: '', inscricao: '', agencia: '', conta: '', observacoes: '',
  };
  const [acessosEmpresa, setAcessosEmpresa] = useState(null);   // empresa-alvo do modal
  const [acessos, setAcessos] = useState([]);
  const [carregandoAcessos, setCarregandoAcessos] = useState(false);
  const [acessoForm, setAcessoForm] = useState(null);
  const [acessoExcluir, setAcessoExcluir] = useState(null);
  const [senhasVisiveis, setSenhasVisiveis] = useState([]);
  // A listagem não traz mais `senha` — só `tem_senha`. O valor é revelado um a
  // um via bporh_credencial (o backend loga quem revelou). Cache: id -> valor.
  const [senhasReveladas, setSenhasReveladas] = useState({});
  const [mostrarSenhaForm, setMostrarSenhaForm] = useState(false);
  const [senhaTocada, setSenhaTocada] = useState(false);
  const API_ACESSO = `${process.env.REACT_APP_API_BASE}/api/bpo/acessos/`;

  const carregarAcessos = useCallback(() => {
    if (!acessosEmpresa) return;
    setCarregandoAcessos(true);
    axios
      .get(API_ACESSO, { params: { area, empresa: acessosEmpresa }, headers: headers() })
      .then((res) => setAcessos(res.data.acessos || []))
      .catch(() => setAcessos([]))
      .finally(() => setCarregandoAcessos(false));
  }, [area, acessosEmpresa]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (acessosEmpresa) carregarAcessos();
    else {
      setAcessoForm(null); setAcessoExcluir(null);
      setSenhasVisiveis([]); setSenhasReveladas({});
    }
  }, [acessosEmpresa, carregarAcessos]);

  const setAc = (campo) => (e) => setAcessoForm(f => ({ ...f, [campo]: e.target.value }));

  // ─── ordem e busca dos acessos (pedido de 13/10/2026) ───
  // a ordem é da equipe, por empresa: o mais usado vai para o início e fica salvo.
  // Só pelas setas — o arrastar saiu em 29/10/2026 (pegava o card ao selecionar texto)
  const [buscaAcesso, setBuscaAcesso] = useState('');
  useEffect(() => { setBuscaAcesso(''); }, [acessosEmpresa]);
  const salvarOrdemAcessos = (lista) => {
    setAcessos(lista);
    axios.post(API_ACESSO, { acao: 'ordenar', area, empresa: acessosEmpresa, ids: lista.map((a) => a.id) },
      { headers: headers() })
      .catch(() => { setMensagem('Não foi possível salvar a ordem dos acessos'); carregarAcessos(); });
  };
  const moverAcesso = (id, destino) => {
    const lista = [...acessos];
    const de = lista.findIndex((a) => a.id === id);
    if (de < 0) return;
    const para = destino === 'inicio' ? 0 : Math.max(0, Math.min(lista.length - 1, de + destino));
    if (para === de) return;
    const [item] = lista.splice(de, 1);
    lista.splice(para, 0, item);
    salvarOrdemAcessos(lista);
  };
  const acessosVisiveis = useMemo(() => {
    const b = buscaAcesso.trim().toLowerCase();
    if (!b) return acessos;
    return acessos.filter((a) => [a.nome, a.tipo, a.usuario, a.operador, a.observacoes]
      .some((x) => String(x || '').toLowerCase().includes(b)));
  }, [acessos, buscaAcesso]);

  const obterSenhaAcesso = (id) => {
    if (senhasReveladas[id] !== undefined) return Promise.resolve(senhasReveladas[id]);
    return buscarCredencial('acesso', id, 'senha').then((valor) => {
      setSenhasReveladas((s) => ({ ...s, [id]: valor }));
      return valor;
    });
  };

  // tira do cache a senha de um registro alterado/excluído — sem isso o olho e
  // o copiar continuariam entregando a credencial antiga depois de uma edição
  const descartarSenhaRevelada = (id) => {
    setSenhasReveladas(({ [id]: _descartada, ...resto }) => resto);
    setSenhasVisiveis((v) => v.filter((x) => x !== id));
  };

  const salvarAcesso = () => {
    if (!acessoForm?.nome?.trim() || !acessoForm?.tipo) {
      setMensagem('Nome e tipo de acesso são obrigatórios');
      return;
    }
    setSalvando(true);
    const payload = { ...acessoForm, area, empresa: acessosEmpresa };
    delete payload.tem_senha;
    // na edição a senha só vai se foi alterada: a listagem não devolve o valor
    // atual, então enviar o campo intocado (vazio) apagaria a senha guardada
    if (acessoForm.id && !senhaTocada) delete payload.senha;
    const req = acessoForm.id
      ? axios.patch(`${API_ACESSO}${acessoForm.id}/`, payload, { headers: headers() })
      : axios.post(API_ACESSO, payload, { headers: headers() });
    req
      .then((res) => {
        setMensagem(`${res.data.mensagem}`);
        if (acessoForm.id) descartarSenhaRevelada(acessoForm.id);
        setAcessoForm(null); carregarAcessos();
      })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Não foi possível salvar'}`))
      .finally(() => setSalvando(false));
  };

  const excluirAcesso = () => {
    if (!acessoExcluir) return;
    setSalvando(true);
    axios
      .delete(`${API_ACESSO}${acessoExcluir.id}/`, { headers: headers() })
      .then(() => {
        setMensagem('Acesso excluído');
        descartarSenhaRevelada(acessoExcluir.id);
        setAcessoExcluir(null); carregarAcessos();
      })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Não foi possível excluir'}`))
      .finally(() => setSalvando(false));
  };

  const copiar = (texto, rotulo) => {
    if (!texto) return;
    navigator.clipboard.writeText(texto)
      .then(() => setMensagem(`${rotulo} copiado`))
      .catch(() => setMensagem('Não foi possível copiar'));
  };

  // ─── Relatório mensal de tempo (com filtros próprios) ───
  const [relAberto, setRelAberto] = useState(false);
  const [relTarefas, setRelTarefas] = useState([]);
  const [carregandoRel, setCarregandoRel] = useState(false);
  const [relFiltros, setRelFiltros] = useState({ mes: mesAtual(), de: '', ate: '', operadora: '', empresa: '' });

  const abrirRelatorio = () => {
    setRelAberto(true);
    setRelFiltros({ mes: mesAtual(), de: '', ate: '', operadora: '', empresa: '' });
    setCarregandoRel(true);
    // busca TODO o período da área uma vez; os filtros são aplicados na tela.
    // Duas fontes: o cronômetro das TAREFAS e o tempo passado NA EMPRESA fora
    // de tarefa (bpo_tempo_cliente) — sem o segundo, quem abre o cliente e
    // trabalha nele sem iniciar tarefa aparecia com tempo zero.
    Promise.all([
      axios.get(API, { params: { area }, headers: headers() }),
      axios.get(API_TEMPO, { params: { area, empresa: '-', relatorio: 1 }, headers: headers() })
        .catch(() => ({ data: { sessoes: [] } })),
    ])
      .then(([resT, resC]) => {
        const tarefas = (resT.data.tarefas || [])
          .filter(t => seg(t.tempo_execucao) > 0)
          .map(t => ({ ...t, _tipo: 'tarefa', _seg: seg(t.tempo_execucao) }));
        const sessoes = (resC.data.sessoes || []).map(x => ({
          _tipo: 'cliente',
          _seg: x.segundos,
          id: `cli-${x.empresa}-${x.usuario}-${x.data_iso}`,
          data_iso: x.data_iso,
          data: x.data_iso.split('-').reverse().join('/'),
          empresa: x.empresa,
          executor: x.usuario,
          responsavel: x.usuario,
          tarefa: 'Tempo na empresa (fora de tarefa)',
          tempo_execucao: hms(x.segundos),
        }));
        setRelTarefas([...tarefas, ...sessoes]);
      })
      .catch(() => setRelTarefas([]))
      .finally(() => setCarregandoRel(false));
  };

  const setRelF = (campo) => (e) => setRelFiltros(f => ({ ...f, [campo]: e.target.value }));

  const relLinhas = useMemo(() => relTarefas.filter(t => {
    // período de datas (quando preenchido) tem prioridade sobre a competência
    if (relFiltros.de || relFiltros.ate) {
      if (relFiltros.de && t.data_iso < relFiltros.de) return false;
      if (relFiltros.ate && t.data_iso > relFiltros.ate) return false;
    } else if (relFiltros.mes && t.data_iso.slice(0, 7) !== relFiltros.mes) return false;
    if (relFiltros.operadora && (t.executor || t.responsavel) !== relFiltros.operadora) return false;
    if (relFiltros.empresa && t.empresa !== relFiltros.empresa) return false;
    return true;
  }), [relTarefas, relFiltros]);

  const relResumo = useMemo(() => {
    const porOp = {}, porEmp = {};
    let totalSeg = 0;
    relLinhas.forEach(t => {
      const sg = t._seg != null ? t._seg : seg(t.tempo_execucao);
      totalSeg += sg;
      const op = t.executor || t.responsavel || 'Sem operadora';
      (porOp[op] = porOp[op] || { nome: op, n: 0, seg: 0 });
      porOp[op].n += 1; porOp[op].seg += sg;
      (porEmp[t.empresa] = porEmp[t.empresa] || { nome: t.empresa, n: 0, seg: 0 });
      porEmp[t.empresa].n += 1; porEmp[t.empresa].seg += sg;
    });
    const ordena = (o) => Object.values(o).sort((a, b) => b.seg - a.seg);
    return { totalSeg, porOperadora: ordena(porOp), porEmpresa: ordena(porEmp) };
  }, [relLinhas]);

  // executores/empresas presentes no relatório (para os selects)
  const relOperadoras = useMemo(() =>
    [...new Set(relTarefas.map(t => t.executor || t.responsavel).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [relTarefas]);
  const relEmpresas = useMemo(() =>
    [...new Set(relTarefas.map(t => t.empresa))].sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [relTarefas]);

  const baixarCsv = () => {
    const esc = (v) => `"${String(v ?? '').replaceAll('"', '""')}"`;
    const linhas = [
      ['Data', 'Empresa', 'Tarefa', 'Operadora', 'Tempo', 'Concluída em'].map(esc).join(';'),
      ...relLinhas.map(t => [
        t.data, t.empresa, t.tarefa, t.executor || t.responsavel || '',
        t.tempo_execucao, t.data_finalizada ? `${t.data_finalizada} ${t.hora_finalizada || ''}`.trim() : '',
      ].map(esc).join(';')),
    ].join('\r\n');
    // BOM: sem ele o Excel abre o CSV com acentuação quebrada
    const blob = new Blob(['﻿' + linhas], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `relatorio_tempo_${area}_${relFiltros.mes || 'periodo'}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // Excel igual ao modal (13/10/2026): Resumo (filtros e total), Por operadora,
  // Por empresa e Detalhe — com o tempo em HH:MM:SS e também em horas decimais,
  // que o Excel soma. Respeita os filtros escolhidos no modal.
  const baixarExcel = () => {
    const horas = (sg) => Math.round((sg / 3600) * 100) / 100;
    const periodo = (relFiltros.de || relFiltros.ate)
      ? `${relFiltros.de ? relFiltros.de.split('-').reverse().join('/') : 'início'} a ${relFiltros.ate ? relFiltros.ate.split('-').reverse().join('/') : 'hoje'}`
      : (relFiltros.mes ? relFiltros.mes.split('-').reverse().join('/') : 'Todo o período');
    const resumo = XLSX.utils.aoa_to_sheet([
      [`Relatório de Tempo — ${ROTULO_AREA[area]}`],
      [],
      ['Período', periodo],
      ['Operadora', relFiltros.operadora || 'Todas'],
      ['Empresa', relFiltros.empresa || 'Todas'],
      ['Registros', relLinhas.length],
      ['Tempo total', hms(relResumo.totalSeg)],
      ['Tempo total (horas)', horas(relResumo.totalSeg)],
      ['Gerado em', new Date().toLocaleString('pt-BR')],
    ]);
    resumo['!cols'] = [{ wch: 22 }, { wch: 40 }];
    const tabelaResumo = (rotulo, lista) => {
      const ws = XLSX.utils.aoa_to_sheet([
        [rotulo, 'Tarefas', 'Tempo total', 'Média', 'Horas (decimal)'],
        ...lista.map((x) => [x.nome, x.n, hms(x.seg), hms(Math.round(x.seg / x.n)), horas(x.seg)]),
        ['Total', lista.reduce((a, x) => a + x.n, 0), hms(relResumo.totalSeg), '', horas(relResumo.totalSeg)],
      ]);
      ws['!cols'] = [{ wch: 36 }, { wch: 9 }, { wch: 12 }, { wch: 12 }, { wch: 15 }];
      return ws;
    };
    const detalhe = XLSX.utils.aoa_to_sheet([
      ['Data', 'Empresa', 'Tarefa', 'Operadora', 'Tempo', 'Horas (decimal)', 'Concluída em'],
      ...relLinhas.slice().sort((a, b) => b.data_iso.localeCompare(a.data_iso)).map((t) => {
        const sg = t._seg != null ? t._seg : seg(t.tempo_execucao);
        return [t.data, t.empresa, t.tarefa, t.executor || t.responsavel || '', t.tempo_execucao, horas(sg),
          t.data_finalizada ? `${t.data_finalizada} ${t.hora_finalizada || ''}`.trim() : ''];
      }),
    ]);
    detalhe['!cols'] = [{ wch: 11 }, { wch: 30 }, { wch: 48 }, { wch: 18 }, { wch: 10 }, { wch: 15 }, { wch: 18 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, resumo, 'Resumo');
    XLSX.utils.book_append_sheet(wb, tabelaResumo('Operadora', relResumo.porOperadora), 'Por operadora');
    XLSX.utils.book_append_sheet(wb, tabelaResumo('Empresa', relResumo.porEmpresa), 'Por empresa');
    XLSX.utils.book_append_sheet(wb, detalhe, 'Detalhe');
    XLSX.writeFile(wb, `relatorio_tempo_${area}_${(relFiltros.de || relFiltros.ate) ? 'periodo' : (relFiltros.mes || 'todo')}.xlsx`);
  };

  // Nova empresa: abre a visão dela vazia — ela passa a existir no painel
  // ao receber a primeira tarefa (a lista de empresas deriva das tarefas)
  const [novaEmpresa, setNovaEmpresa] = useState(null);   // null | {nome, operadora}
  const [criandoEmpresa, setCriandoEmpresa] = useState(false);

  // grava a empresa ANTES de abrir: sem isso ela só existia depois da primeira
  // tarefa e sumia no F5 — e o mesmo nome digitado diferente virava duas
  const criarEmpresa = () => {
    const nome = novaEmpresa.nome.trim().replace(/\s+/g, ' ');
    setCriandoEmpresa(true);
    axios
      .post(`${process.env.REACT_APP_API_BASE}/api/bpo/empresas/criar/`,
        { area, empresa: nome, operadora: novaEmpresa.operadora }, { headers: headers() })
      .then((res) => {
        setMensagem(`${res.data.mensagem}`);
        setOperadoraPadrao(novaEmpresa.operadora);
        setEmpresaSelecionada(res.data.empresa || nome);
        setNovaEmpresa(null);
        carregar();
      })
      .catch((err) => setMensagem(
        `${err.response?.data?.mensagem || 'Não foi possível criar a empresa'}`))
      .finally(() => setCriandoEmpresa(false));
  };
  // operadora escolhida na criação: preenche o 'quem executa' das tarefas novas
  const [operadoraPadrao, setOperadoraPadrao] = useState('');

  // ─── Anotações da empresa (equivalente PlayBPO) ───
  const [mostrarAnotacoes, setMostrarAnotacoes] = useState(false);
  const [anotacoes, setAnotacoes] = useState([]);
  const [carregandoAnot, setCarregandoAnot] = useState(false);
  const [anotForm, setAnotForm] = useState(null);        // {id?, assunto, anotacao}
  const [anotExcluir, setAnotExcluir] = useState(null);
  const API_ANOT = `${process.env.REACT_APP_API_BASE}/api/bpo/anotacoes/`;

  const carregarAnotacoes = useCallback(() => {
    if (!empresaSelecionada) return;
    setCarregandoAnot(true);
    axios
      .get(API_ANOT, { params: { area, empresa: empresaSelecionada }, headers: headers() })
      .then((res) => setAnotacoes(res.data.anotacoes || []))
      .catch(() => setAnotacoes([]))
      .finally(() => setCarregandoAnot(false));
  }, [area, empresaSelecionada]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mostrarAnotacoes) carregarAnotacoes();
    else { setAnotForm(null); setAnotExcluir(null); }
  }, [mostrarAnotacoes, carregarAnotacoes]);

  const salvarAnotacao = () => {
    if (!anotForm?.assunto?.trim() || !anotForm?.anotacao?.trim()) {
      setMensagem('Assunto e anotação são obrigatórios');
      return;
    }
    setSalvando(true);
    const req = anotForm.id
      ? axios.patch(`${API_ANOT}${anotForm.id}/`,
          { assunto: anotForm.assunto, anotacao: anotForm.anotacao }, { headers: headers() })
      : axios.post(API_ANOT,
          { area, empresa: empresaSelecionada, assunto: anotForm.assunto, anotacao: anotForm.anotacao },
          { headers: headers() });
    req
      .then((res) => { setMensagem(`${res.data.mensagem}`); setAnotForm(null); carregarAnotacoes(); })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Não foi possível salvar'}`))
      .finally(() => setSalvando(false));
  };

  const excluirAnotacao = () => {
    if (!anotExcluir) return;
    setSalvando(true);
    axios
      .delete(`${API_ANOT}${anotExcluir.id}/`, { headers: headers() })
      .then(() => { setMensagem('Anotação excluída'); setAnotExcluir(null); carregarAnotacoes(); })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Não foi possível excluir'}`))
      .finally(() => setSalvando(false));
  };

  const confirmarExclusao = () => {
    if (!excluir) return;
    setSalvando(true);
    axios
      .delete(`${API}${excluir.id}/${pararRecorrencia ? '?recorrencia=1' : ''}`, { headers: headers() })
      .then(() => { setMensagem('Tarefa excluída'); setExcluir(null); carregar(true); })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Não foi possível excluir'}`))
      .finally(() => setSalvando(false));
  };

  const concluida = (t) => t.status === 'Finalizada' || t.status === 'Finalizada em atraso';

  return (
    <div className="bpo-tarefas-page">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        {/* Cabeçalho */}
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-calendar-check header-icon me-2"></i>
            <h2 className="titulo-pagina m-0">
              {ROTULO_AREA[area]} — Atividades Mensais
              {empresaSelecionada && <span className="text-muted"> · {empresaSelecionada}</span>}
            </h2>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {empresaSelecionada && (
              <button className="btn btn-outline-secondary"
                onClick={() => { setEmpresaSelecionada(null); setFiltroStatus(''); setFiltroResponsavel(''); setOperadoraPadrao(''); }}>
                <i className="fas fa-arrow-left me-2"></i>
                Empresas
              </button>
            )}
            <select className="form-select" style={{ width: 'auto' }} value={mes}
              onChange={(e) => setMes(e.target.value)}
              title="Competência (pendências de meses anteriores continuam aparecendo)">
              {[...new Set([mesSeguinte(), mesAtual(), ...meses])].sort().reverse().map(m => (
                <option key={m} value={m}>
                  {m.split('-').reverse().join('/')}{m === mesSeguinte() ? ' (próximo)' : ''}
                </option>
              ))}
              <option value="">Todo o período</option>
            </select>
            <style>{`
              @keyframes incluirHorasPulso {
                0% { box-shadow: 0 0 0 0 rgba(241, 190, 72, .75); }
                70% { box-shadow: 0 0 0 12px rgba(241, 190, 72, 0); }
                100% { box-shadow: 0 0 0 0 rgba(241, 190, 72, 0); }
              }
              .btn-incluir-horas { animation: incluirHorasPulso 2s infinite; }
              .btn-incluir-horas:hover { animation: none; transform: translateY(-1px); }
              @media (prefers-reduced-motion: reduce) { .btn-incluir-horas { animation: none; } }
            `}</style>
            <button className="btn btn-incluir-horas fw-bold px-3"
              style={{ background: '#f1be48', color: '#00205b', border: '1px solid #d9a52e' }}
              onClick={() => (empresaSelecionada ? abrirIncluirHoras(empresaSelecionada) : setHorasEmpresa(''))}
              title="Esqueceu de cronometrar? Lance aqui as horas trabalhadas">
              <i className="fas fa-clock-rotate-left me-2"></i>
              Incluir horas
            </button>
            {empresaSelecionada ? (
              <button className="btn btn-primary" onClick={abrirNova}>
                <i className="fas fa-plus me-2"></i>
                Nova Tarefa
              </button>
            ) : (
              <>
                <button className="btn btn-outline-primary" onClick={abrirRelatorio}>
                  <i className="fas fa-file-lines me-2"></i>
                  Relatório de Tempo
                </button>
                <button className="btn btn-primary" onClick={() => setNovaEmpresa({ nome: '', operadora: '' })}>
                  <i className="fas fa-building me-2"></i>
                  Nova Empresa
                </button>
              </>
            )}
            <button className="btn btn-outline-primary" onClick={() => carregar()} disabled={carregando} title="Atualizar">
              <i className={`fas fa-sync-alt ${carregando ? 'fa-spin' : ''}`}></i>
            </button>
          </div>
        </div>

        {carregando ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="text-muted mt-2 mb-0">Carregando tarefas...</p>
          </div>
        ) : !empresaSelecionada ? (
          <>
            {/* ─── VISÃO 1: EMPRESAS ─── */}
            <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
              <span className="badge fs-6" style={{ backgroundColor: '#00205b', fontVariantNumeric: 'tabular-nums' }}>
                {totais.total} tarefas
              </span>
              <span className="badge bg-danger" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {totais.vencidas} vencidas
              </span>
              <span className="badge bg-secondary" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {totais.aVencer} a vencer
              </span>
              <span className="badge bg-success" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {totais.concluidas} concluídas
              </span>
            </div>

            {/* ─── FILTROS ─── */}
            <div className="row g-2 mb-3">
              <div className="col-md-3">
                <label className="form-label small fw-bold mb-1 text-muted">Empresa</label>
                <select className="form-select form-select-sm" value={filtroEmpresa}
                  onChange={(e) => setFiltroEmpresa(e.target.value)}>
                  <option value="">Todas as empresas</option>
                  {empresasOpcoes.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-bold mb-1 text-muted">Operadora</label>
                <select className="form-select form-select-sm" value={filtroResponsavel}
                  onChange={(e) => setFiltroResponsavel(e.target.value)}>
                  <option value="">Todas as operadoras</option>
                  {responsaveis.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-bold mb-1 text-muted">Status</label>
                <select className="form-select form-select-sm" value={filtroStatus}
                  onChange={(e) => setFiltroStatus(e.target.value)}>
                  <option value="">Todos os status</option>
                  {Object.keys(CORES_STATUS).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label small fw-bold mb-1 text-muted">Buscar</label>
                <input type="text" className="form-control form-control-sm"
                  placeholder="Tarefa ou empresa..." value={busca}
                  onChange={(e) => setBusca(e.target.value)} />
              </div>
            </div>

            {/* ─── DASH (mesmo desenho da página Contabilidade) ─── */}
            {tarefasV1.length > 0 && (
              <div className="mb-4 p-3 border rounded bg-light">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                  <h5 className="m-0">
                    <i className="fas fa-chart-pie me-2"></i>
                    Desempenho{filtroResponsavel ? ` da Operadora: ${filtroResponsavel}` : ` — ${ROTULO_AREA[area]}`}
                    {mes && ` · ${mes.split('-').reverse().join('/')}`}
                  </h5>
                  {/* % finalizado = concluídas (no prazo ou não) sobre o total */}
                  {totais.total > 0 && (() => {
                    const pct = (totais.concluidas / totais.total) * 100;
                    return (
                      <div className="d-flex align-items-center gap-2" title={`${totais.concluidas} de ${totais.total} tarefas concluídas`}>
                        <div className="progress" style={{ width: '140px', height: '10px' }}>
                          <div className="progress-bar"
                            style={{ width: `${Math.max(pct, totais.concluidas > 0 ? 2 : 0)}%`,
                              backgroundColor: pct === 100 ? '#1fa971' : '#00b5cc' }} />
                        </div>
                        <span className="fw-bold fs-5"
                          style={{ color: pct === 100 ? '#1fa971' : '#00205b', fontVariantNumeric: 'tabular-nums' }}>
                          {pctTexto(pct)}%
                        </span>
                        <span className="text-muted small">finalizado</span>
                      </div>
                    );
                  })()}
                </div>

                <div className="row">
                  {/* Pizza — distribuição de status */}
                  <div className="col-md-6">
                    <h6 className="mb-2">Distribuição de Status</h6>
                    {distStatus.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={distStatus}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            innerRadius={60}
                            paddingAngle={4}
                            cornerRadius={10}
                            isAnimationActive={true}
                            label={({ name, percent }) => `${name}: ${pctTexto(percent * 100)}%`}
                            stroke="#fff"
                          >
                            {distStatus.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={CORES_PIZZA[entry.name] || '#6c757d'} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#00205b',
                              border: 'none',
                              borderRadius: '8px',
                              color: '#fff',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                            }}
                            formatter={(value, name) => [`${value}`, name]}
                          />
                          <Legend
                            verticalAlign="bottom"
                            iconSize={14}
                            wrapperStyle={{ marginTop: '8px', fontSize: '12px', fontWeight: '500' }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-muted mt-4 text-center">Nenhum dado disponível.</p>
                    )}
                  </div>

                  {/* Colunas — pendências por prazo */}
                  <div className="col-md-6">
                    <h6 className="mb-2">Pendências por Prazo</h6>
                    {prazoBuckets.some(b => b.value > 0) ? (
                      <ResponsiveContainer width="100%" height={230}>
                        <BarChart data={prazoBuckets} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis allowDecimals={false} />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#00205b',
                              border: 'none',
                              borderRadius: '8px',
                              color: '#fff',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                            }}
                            formatter={(value) => [`${value} tarefa(s)`, 'Quantidade']}
                          />
                          <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                            {prazoBuckets.map((entry, index) => (
                              <Cell key={`bar-cell-${index}`} fill={entry.color} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-muted mt-4 text-center">Nenhuma pendência com prazo.</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {empresas.length === 0 ? (
              <div className="alert alert-info">
                <i className="fas fa-info-circle me-2"></i>
                Nenhuma tarefa nesta competência.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-striped table-hover align-middle">
                  <thead>
                    <tr>
                      <th><i className="fas fa-building me-2"></i>Empresa</th>
                      <th className="text-center"><i className="fas fa-list-check me-2"></i>Tarefas</th>
                      <th className="text-center"><i className="fas fa-triangle-exclamation me-2"></i>Vencidas</th>
                      <th className="text-center"><i className="fas fa-hourglass-half me-2"></i>A vencer</th>
                      <th className="text-center"><i className="fas fa-circle-check me-2"></i>Concluídas</th>
                      <th className="text-center"><i className="fas fa-stopwatch me-2"></i>Tempo médio</th>
                      <th className="text-center"><i className="fas fa-key me-2"></i>Acessos</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {empresas.map((e) => (
                      <tr key={e.empresa} style={{ cursor: 'pointer' }}
                        onClick={() => setEmpresaSelecionada(e.empresa)}>
                        <td className="fw-bold">
                          {/* farol: vermelho com vencida; verde tudo concluído; cinza no prazo */}
                          <i className={`fas fa-circle me-2 ${
                            e.vencidas > 0 ? 'text-danger'
                              : e.aVencer === 0 ? 'text-success' : 'text-secondary'}`}
                            style={{ fontSize: '.6rem' }}></i>
                          {e.empresa}
                        </td>
                        <td className="text-center" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          <span className="badge bg-light text-dark border">{e.total}</span>
                        </td>
                        <td className="text-center" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {e.vencidas > 0
                            ? <span className="badge bg-danger">{e.vencidas}</span>
                            : <span className="text-muted">-</span>}
                        </td>
                        <td className="text-center" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {e.aVencer > 0
                            ? <span className="badge bg-secondary">{e.aVencer}</span>
                            : <span className="text-muted">-</span>}
                        </td>
                        <td className="text-center" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {e.concluidas > 0
                            ? <span className="badge bg-success">{e.concluidas}</span>
                            : <span className="text-muted">-</span>}
                        </td>
                        <td className="text-center text-muted"
                          style={{ fontVariantNumeric: 'tabular-nums', fontSize: '.85rem', whiteSpace: 'nowrap' }}
                          title="Média do tempo cronometrado nas tarefas desta empresa">
                          {e.tempoN > 0 ? hms(Math.round(e.tempoSeg / e.tempoN)) : '-'}
                          <button className="btn btn-sm btn-light p-1 ms-2 border"
                            onClick={(ev) => { ev.stopPropagation(); abrirHistorico(e.empresa); }}
                            title="Histórico de tempo desta empresa (todas as competências)">
                            <i className="fas fa-stopwatch text-primary"></i>
                          </button>
                        </td>
                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                          <button className="btn btn-sm btn-outline-primary me-1"
                            onClick={(ev) => { ev.stopPropagation(); setAcessosEmpresa(e.empresa); }}
                            title="Acessos desta empresa: bancos, drives, sistemas e senhas">
                            <i className="fas fa-key"></i>
                          </button>
                          <button className="btn btn-sm btn-outline-info me-1"
                            onClick={(ev) => { ev.stopPropagation(); abrirDadosEmpresa(e.empresa); }}
                            title="Dados da empresa: razão social, CNPJ, regime tributário...">
                            <i className="fas fa-building"></i>
                          </button>
                          <button className="btn btn-sm btn-outline-secondary"
                            onClick={(ev) => { ev.stopPropagation(); abrirEdicaoEmpresa(e.empresa); }}
                            title="Editar a empresa (nome, operadora e dados cadastrais)">
                            <i className="fas fa-edit"></i>
                          </button>
                        </td>
                        <td className="text-end text-primary small">
                          Ver tarefas <i className="fas fa-chevron-right ms-1"></i>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* ─── EMPRESAS INATIVAS (para reativar) ─── */}
            {empresasInativas.length > 0 && (
              <details className="mt-3">
                <summary className="text-muted small" style={{ cursor: 'pointer' }}>
                  <i className="fas fa-ban me-1"></i>
                  Empresas inativas ({empresasInativas.length}) — clique para ver e reativar
                </summary>
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {empresasInativas.map((nome) => (
                    <span key={nome} className="badge bg-light text-dark border d-inline-flex align-items-center gap-2 p-2">
                      {nome}
                      <button className="btn btn-sm btn-outline-success py-0 px-1"
                        onClick={() => alternarAtivacao(nome, true)} disabled={salvando}
                        title="Reativar — volta ao painel e às recorrências">
                        <i className="fas fa-rotate-left" style={{ fontSize: '.7rem' }}></i>
                      </button>
                    </span>
                  ))}
                </div>
              </details>
            )}
          </>
        ) : (
          <>
            {/* ─── VISÃO 2: TAREFAS DA EMPRESA ─── */}
            <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
              {Object.keys(CORES_STATUS).map((s) => {
                const n = tarefas.filter(t => t.empresa === empresaSelecionada && t.status === s).length;
                return (
                  <button key={s}
                    className={`btn btn-sm ${filtroStatus === s ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setFiltroStatus(filtroStatus === s ? '' : s)}>
                    {s} <span className="badge bg-light text-dark ms-1">{n}</span>
                  </button>
                );
              })}
              {tempoCliente && (
                <span
                  className="badge d-inline-flex align-items-center ms-auto"
                  title={(tarefaRodando
                    ? 'Pausado — o relógio está na tarefa em execução'
                    : 'Sessão atual neste cliente (fora das tarefas) — parar salva no histórico e a próxima começa do zero')
                    + (tempoCliente.hoje_seg ? ` · total de hoje: ${hms(tempoCliente.hoje_seg)}` : '')}
                  style={{
                    fontVariantNumeric: 'tabular-nums',
                    fontSize: '.85rem',
                    backgroundColor: tempoCliente.rodando ? '#e6f7ef' : '#f2f4f8',
                    color: tempoCliente.rodando ? '#116b47' : '#6c757d',
                    border: `1.5px solid ${tempoCliente.rodando ? '#1fa971' : '#cbd5e1'}`,
                  }}>
                  <i className={`fas ${tempoCliente.rodando ? 'fa-stopwatch' : 'fa-pause'} me-2`}></i>
                  cliente {hms(tempoClienteAtual())}
                  {tarefaRodando && <span className="ms-1 fw-normal">(na tarefa)</span>}
                </span>
              )}
              <button className={`btn btn-sm btn-outline-primary ${tempoCliente ? '' : 'ms-auto'}`}
                onClick={() => abrirDadosEmpresa(empresaSelecionada)}
                title="Dados da empresa: razão social, CNPJ, regime tributário...">
                <i className="fas fa-building me-2"></i>
                Dados da empresa
              </button>
              <button className="btn btn-sm btn-outline-primary"
                onClick={() => setMostrarAnotacoes(true)}
                title="Anotações desta empresa: contatos, regras combinadas, particularidades">
                <i className="fas fa-note-sticky me-2"></i>
                Anotações
              </button>
              <button className="btn btn-sm btn-outline-primary"
                onClick={() => setAcessosEmpresa(empresaSelecionada)}
                title="Acessos desta empresa: bancos, drives, sistemas e senhas">
                <i className="fas fa-key me-2"></i>
                Acessos
              </button>
              <select className="form-select form-select-sm" style={{ maxWidth: '220px' }}
                value={filtroResponsavel} onChange={(e) => setFiltroResponsavel(e.target.value)}>
                <option value="">Todas as operadoras</option>
                {responsaveis.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>

            <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
              <div className="btn-group btn-group-sm" role="group" aria-label="Dia das tarefas">
                <button type="button" className="btn btn-outline-secondary" onClick={() => moverDia(-1)}
                  title="Dia anterior" aria-label="Dia anterior"><i className="fas fa-chevron-left"></i></button>
                <button type="button" className={`btn ${diaVer === hojeIso ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setDiaVer(hojeIso)}>Hoje</button>
                <button type="button" className="btn btn-outline-secondary" onClick={() => moverDia(1)}
                  title="Próximo dia" aria-label="Próximo dia"><i className="fas fa-chevron-right"></i></button>
              </div>
              <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }}
                value={diaVer} onChange={(e) => setDiaVer(e.target.value)} aria-label="Escolher o dia" />
              <button type="button" className={`btn btn-sm ${!diaVer ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setDiaVer('')}>Todas as tarefas</button>
              <span className="small text-muted">
                {diaVer
                  ? (diaVer === hojeIso
                    ? 'Tarefas de hoje e as atrasadas que ainda estão abertas'
                    : `Tarefas de ${diaVer.split('-').reverse().join('/')}`)
                  : 'Todas as tarefas do mês, com as de hoje primeiro'}
              </span>
            </div>

            {tarefasEmpresa.length === 0 ? (
              <div className="alert alert-info">
                <i className="fas fa-info-circle me-2"></i>
                {diaVer ? 'Nenhuma tarefa para este dia. Use "Todas as tarefas" para ver o mês inteiro.'
                  : 'Nenhuma tarefa para os filtros selecionados.'}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-striped table-hover align-middle">
                  <thead>
                    <tr>
                      <th style={{ whiteSpace: 'nowrap' }}><i className="fas fa-calendar-day me-2"></i>Data</th>
                      <th><i className="fas fa-tasks me-2"></i>Tarefa</th>
                      <th><i className="fas fa-user-tie me-2"></i>Operadora</th>
                      <th><i className="fas fa-flag me-2"></i>Status</th>
                      <th style={{ whiteSpace: 'nowrap' }}><i className="fas fa-stopwatch me-2"></i>Tempo</th>
                      <th><i className="fas fa-check-double me-2"></i>Concluída</th>
                      <th className="text-center">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tarefasEmpresa.map((t) => (
                      <tr key={t.id}>
                        <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                          {t.data}
                          {t.data_iso === hojeIso && !concluida(t) && (
                            <span className="badge ms-2" style={{ background: '#fff5da', color: '#8a6d00', border: '1px solid #f1be48' }}>hoje</span>
                          )}
                          {mes && t.data_iso < `${mes}-01` && (
                            <span className="badge bg-light text-danger border ms-2"
                              title="Pendência de mês anterior — aparece até ser concluída">
                              mês anterior
                            </span>
                          )}
                        </td>
                        <td className="fw-bold" style={{ cursor: 'pointer' }}
                          onClick={() => abrirDetalhe(t)}
                          title="Ver descrição da tarefa">
                          {t.tarefa}
                          {(t.origem === 'recorrencia' || t.origem === 'playbpo') && (
                            <span className="badge bg-light text-primary border ms-2"
                              style={{ fontSize: '.68rem' }} title="Tarefa recorrente — repete todo mês">
                              Mensal
                            </span>
                          )}
                          {t.descricao && <i className="fas fa-align-left text-muted ms-2" style={{ fontSize: '.75rem' }}
                            title="Tem descrição"></i>}
                        </td>
                        <td>{t.responsavel || <span className="text-muted">-</span>}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <span className={`badge ${CORES_STATUS[t.status]?.classe || 'bg-light text-dark'}`}>
                            <i className={`fas ${CORES_STATUS[t.status]?.icone || 'fa-circle'} me-1`}></i>
                            {t.status}
                          </span>
                          {t.dias_atraso > 0 && (
                            <span className={`ms-2 small fw-bold ${
                              t.status === 'Atrasada' ? 'text-danger' : 'text-muted'}`}
                              style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {t.dias_atraso} dia{t.dias_atraso > 1 ? 's' : ''}
                            </span>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {/* cronômetro: liga/desliga e acumula no banco */}
                          {!concluida(t) && (
                            <button
                              className={`btn btn-sm me-2 ${t.iniciado_em ? 'btn-danger' : 'btn-outline-primary'}`}
                              onClick={() => alternarCronometro(t)} disabled={acaoId === t.id}
                              title={t.iniciado_em ? 'Parar cronômetro' : 'Iniciar cronômetro'}>
                              {acaoId === t.id
                                ? <span className="spinner-border spinner-border-sm"></span>
                                : <i className={`fas ${t.iniciado_em ? 'fa-stop' : 'fa-play'}`}></i>}
                            </button>
                          )}
                          <span className={t.iniciado_em ? 'fw-bold text-danger' : 'text-muted'}
                            style={{ fontVariantNumeric: 'tabular-nums', fontSize: '.9rem' }}>
                            {tempoAtual(t) > 0 || t.iniciado_em ? hms(tempoAtual(t)) : '-'}
                          </span>
                        </td>
                        <td className="text-muted" style={{ fontSize: '.82rem', whiteSpace: 'nowrap' }}>
                          {t.data_finalizada ? (
                            <>
                              {t.data_finalizada}{t.hora_finalizada && ` ${t.hora_finalizada}`}
                              {t.executor && <div>por {t.executor}</div>}
                            </>
                          ) : '-'}
                        </td>
                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                          {concluida(t) ? (
                            <button className="btn btn-sm btn-outline-secondary me-1"
                              onClick={() => reabrirTarefa(t)} disabled={acaoId === t.id}
                              title="Reabrir tarefa">
                              <i className="fas fa-rotate-left"></i>
                            </button>
                          ) : (
                            <button className="btn btn-sm btn-outline-success me-1"
                              onClick={() => concluirTarefa(t)} disabled={acaoId === t.id}
                              title="Marcar como concluída">
                              <i className="fas fa-check"></i>
                            </button>
                          )}
                          <button className="btn btn-sm btn-outline-primary me-1"
                            onClick={() => abrirEdicao(t)} title="Editar">
                            <i className="fas fa-edit"></i>
                          </button>
                          <button className="btn btn-sm btn-outline-danger"
                            onClick={() => setExcluir(t)} title="Excluir">
                            <i className="fas fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* MODAL: RELATÓRIO MENSAL DE TEMPO */}
      {relAberto && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, overflowY: 'auto' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '1100px' }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-file-lines me-2"></i>
                    Relatório de Tempo — {ROTULO_AREA[area]}
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {carregandoRel ? 'Carregando...'
                      : `${relLinhas.length} registro(s) cronometrado(s) · tempo total ${hms(relResumo.totalSeg)}`}
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setRelAberto(false)}></button>
              </div>

              <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {/* filtros */}
                <div className="row g-2 mb-3">
                  <div className="col-md-2">
                    <label className="form-label small fw-bold mb-1 text-muted">Competência</label>
                    <select className="form-select form-select-sm" value={relFiltros.mes}
                      disabled={Boolean(relFiltros.de || relFiltros.ate)}
                      onChange={setRelF('mes')}>
                      <option value="">Todo o período</option>
                      {[...new Set([mesAtual(), ...meses])].sort().reverse().map(m => (
                        <option key={m} value={m}>{m.split('-').reverse().join('/')}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label className="form-label small fw-bold mb-1 text-muted">De</label>
                    <input type="date" className="form-control form-control-sm"
                      value={relFiltros.de} max={relFiltros.ate || undefined} onChange={setRelF('de')} />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label small fw-bold mb-1 text-muted">Até</label>
                    <input type="date" className="form-control form-control-sm"
                      value={relFiltros.ate} min={relFiltros.de || undefined} onChange={setRelF('ate')} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small fw-bold mb-1 text-muted">Operadora</label>
                    <select className="form-select form-select-sm" value={relFiltros.operadora}
                      onChange={setRelF('operadora')}>
                      <option value="">Todas</option>
                      {relOperadoras.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small fw-bold mb-1 text-muted">Empresa</label>
                    <select className="form-select form-select-sm" value={relFiltros.empresa}
                      onChange={setRelF('empresa')}>
                      <option value="">Todas</option>
                      {relEmpresas.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                </div>
                {(relFiltros.de || relFiltros.ate) && (
                  <div className="alert alert-info py-2 small">
                    <i className="fas fa-filter me-2"></i>
                    Filtrando pelo período de datas — a competência fica ignorada enquanto De/Até estiverem preenchidos.
                  </div>
                )}

                {carregandoRel ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status"></div>
                  </div>
                ) : relLinhas.length === 0 ? (
                  <div className="alert alert-info mb-0">
                    <i className="fas fa-info-circle me-2"></i>
                    Nenhum tempo cronometrado nos filtros selecionados.
                  </div>
                ) : (
                  <>
                    {/* resumo por operadora e por empresa */}
                    <div className="row g-3 mb-3">
                      <div className="col-md-6">
                        <h6 className="fw-bold" style={{ color: '#00205b' }}>
                          <i className="fas fa-user-tie me-2"></i>Por operadora
                        </h6>
                        <table className="table table-sm table-striped align-middle mb-0">
                          <thead>
                            <tr>
                              <th>Operadora</th>
                              <th className="text-end">Tarefas</th>
                              <th className="text-end">Tempo total</th>
                              <th className="text-end">Média</th>
                            </tr>
                          </thead>
                          <tbody>
                            {relResumo.porOperadora.map(o => (
                              <tr key={o.nome}>
                                <td className="fw-bold">{o.nome}</td>
                                <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>{o.n}</td>
                                <td className="text-end fw-bold" style={{ fontVariantNumeric: 'tabular-nums', color: '#00205b' }}>
                                  {hms(o.seg)}
                                </td>
                                <td className="text-end text-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {hms(Math.round(o.seg / o.n))}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="col-md-6">
                        <h6 className="fw-bold" style={{ color: '#00205b' }}>
                          <i className="fas fa-building me-2"></i>Por empresa
                        </h6>
                        <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                          <table className="table table-sm table-striped align-middle mb-0">
                            <thead>
                              <tr>
                                <th>Empresa</th>
                                <th className="text-end">Tarefas</th>
                                <th className="text-end">Tempo total</th>
                                <th className="text-end">Média</th>
                              </tr>
                            </thead>
                            <tbody>
                              {relResumo.porEmpresa.map(e => (
                                <tr key={e.nome}>
                                  <td className="fw-bold">{e.nome}</td>
                                  <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>{e.n}</td>
                                  <td className="text-end fw-bold" style={{ fontVariantNumeric: 'tabular-nums', color: '#00205b' }}>
                                    {hms(e.seg)}
                                  </td>
                                  <td className="text-end text-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                    {hms(Math.round(e.seg / e.n))}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {/* detalhe */}
                    <h6 className="fw-bold" style={{ color: '#00205b' }}>
                      <i className="fas fa-list me-2"></i>Detalhe
                    </h6>
                    <div className="table-responsive">
                      <table className="table table-sm table-striped align-middle mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: '100px' }}>Data</th>
                            <th>Empresa</th>
                            <th>Tarefa</th>
                            <th>Operadora</th>
                            <th className="text-end" style={{ width: '100px' }}>Tempo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {relLinhas
                            .slice()
                            .sort((a, b) => b.data_iso.localeCompare(a.data_iso))
                            .map(t => (
                              <tr key={t.id}>
                                <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{t.data}</td>
                                <td>{t.empresa}</td>
                                <td>
                                  {t._tipo === 'cliente' ? (
                                    <span className="text-muted">
                                      <i className="fas fa-building me-2"></i>{t.tarefa}
                                    </span>
                                  ) : t.tarefa}
                                </td>
                                <td>{t.executor || t.responsavel || <span className="text-muted">-</span>}</td>
                                <td className="text-end fw-bold"
                                  style={{ fontVariantNumeric: 'tabular-nums', color: '#00205b' }}>
                                  {t.tempo_execucao}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>

              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-outline-success me-auto" onClick={baixarCsv}
                  disabled={carregandoRel || relLinhas.length === 0}>
                  <i className="fas fa-file-csv me-2"></i>
                  Baixar CSV
                </button>
                <button type="button" className="btn btn-success" onClick={baixarExcel}
                  disabled={carregandoRel || relLinhas.length === 0}>
                  <i className="fas fa-file-excel me-2"></i>
                  Baixar Excel
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setRelAberto(false)}>
                  <i className="fas fa-times me-2"></i>Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: ATALHO 'INCLUIR HORAS' — escolhe a empresa e abre o histórico com o formulário */}
      {horasEmpresa !== null && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-clock-rotate-left me-2"></i>Incluir horas
                  </h5>
                  <small style={{ color: '#6c757d' }}>Em qual empresa você trabalhou?</small>
                </div>
                <button type="button" className="btn-close" onClick={() => setHorasEmpresa(null)}></button>
              </div>
              <div className="modal-body">
                <label className="form-label small fw-bold mb-1" htmlFor="horas-empresa">Empresa</label>
                <select id="horas-empresa" className="form-select" value={horasEmpresa}
                  onChange={(e) => setHorasEmpresa(e.target.value)}>
                  <option value="">Escolha a empresa...</option>
                  {todasEmpresas.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <div className="small text-muted mt-2">
                  Em seguida você informa a data, as horas (HH:MM) e se foi numa tarefa ou fora delas.
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setHorasEmpresa(null)}>Cancelar</button>
                <button type="button" className="btn btn-success" disabled={!horasEmpresa}
                  onClick={() => abrirIncluirHoras(horasEmpresa)}>
                  <i className="fas fa-arrow-right me-2"></i>Continuar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: HISTÓRICO DE TEMPO DA EMPRESA */}
      {histEmpresa && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, overflowY: 'auto' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '980px' }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-stopwatch me-2"></i>
                    Histórico de tempo — {histEmpresa}
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {carregandoHist
                      ? 'Carregando...'
                      : `${histTarefas.length} tarefa(s) cronometrada(s)`
                        + (histCliente.length ? ' + tempo no cliente' : '')
                        + ` · tempo total ${hms(histTotalSeg)}`}
                    {' · todas as competências'}
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setHistEmpresa(null)}></button>
              </div>

              <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                {!carregandoHist && (incluirHoras ? (
                  <div className="border rounded p-3 mb-3" style={{ background: '#f4f8fc' }}>
                    <div className="fw-bold mb-2" style={{ color: '#212529' }}>
                      <i className="fas fa-clock-rotate-left me-2 text-primary"></i>Incluir horas esquecidas
                    </div>
                    <div className="row g-2 align-items-end">
                      <div className="col-md-3">
                        <label className="form-label small fw-bold mb-1" htmlFor="incl-dia">Data</label>
                        <input id="incl-dia" type="date" className="form-control form-control-sm" max={hojeLocal()}
                          value={incluirHoras.dia} disabled={salvando}
                          onChange={(e) => setIncluirHoras(f => ({ ...f, dia: e.target.value }))} />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label small fw-bold mb-1" htmlFor="incl-horas">Horas</label>
                        <input id="incl-horas" type="text" className="form-control form-control-sm" placeholder="HH:MM"
                          value={incluirHoras.horas} disabled={salvando}
                          onChange={(e) => setIncluirHoras(f => ({ ...f, horas: e.target.value, erro: '' }))} />
                        {leituraHoras(incluirHoras.horas).texto && (
                          <div className={`small ${leituraHoras(incluirHoras.horas).ok ? 'text-muted' : 'text-danger fw-semibold'}`}>
                            {leituraHoras(incluirHoras.horas).texto}
                          </div>
                        )}
                      </div>
                      <div className="col-md-7">
                        <label className="form-label small fw-bold mb-1" htmlFor="incl-onde">Onde</label>
                        <select id="incl-onde" className="form-select form-select-sm" value={incluirHoras.tarefa_id}
                          disabled={salvando}
                          onChange={(e) => setIncluirHoras(f => ({ ...f, tarefa_id: e.target.value }))}>
                          <option value="">Tempo no cliente (fora de tarefas) — em seu nome</option>
                          {histTodas.filter(t => !t.iniciado_em).map(t => (
                            <option key={t.id} value={t.id}>{t.data} · {t.tarefa}{t.tempo_execucao ? ` (${t.tempo_execucao})` : ''}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-9">
                        <label className="form-label small fw-bold mb-1" htmlFor="incl-motivo">Motivo (opcional)</label>
                        <input id="incl-motivo" type="text" className="form-control form-control-sm"
                          placeholder="Ex.: esqueci de iniciar o cronômetro" value={incluirHoras.motivo} disabled={salvando}
                          onChange={(e) => setIncluirHoras(f => ({ ...f, motivo: e.target.value }))} />
                      </div>
                      <div className="col-md-3 d-flex gap-2">
                        <button type="button" className="btn btn-sm btn-success flex-fill" onClick={salvarInclusaoHoras}
                          disabled={salvando}>
                          {salvando ? <span className="spinner-border spinner-border-sm"></span>
                            : <><i className="fas fa-plus me-1"></i>Incluir</>}
                        </button>
                        <button type="button" className="btn btn-sm btn-secondary" onClick={() => setIncluirHoras(null)}
                          disabled={salvando}>Cancelar</button>
                      </div>
                    </div>
                    {incluirHoras.erro && (
                      <div className="alert alert-danger py-1 px-2 small mt-2 mb-0">
                        <i className="fas fa-triangle-exclamation me-1"></i>{incluirHoras.erro}
                      </div>
                    )}
                    <div className="small text-muted mt-2">
                      As horas são <b>somadas</b> ao que já existe. Na tarefa, o cronômetro precisa estar parado.
                      Cada inclusão fica registrada com quem lançou e o motivo.
                    </div>
                  </div>
                ) : (
                  <div className="mb-3">
                    <button type="button" className="btn btn-sm btn-outline-primary"
                      onClick={() => setIncluirHoras({ dia: hojeLocal(), horas: '', tarefa_id: '', motivo: '' })}>
                      <i className="fas fa-plus me-2"></i>Incluir horas
                    </button>
                    <span className="small text-muted ms-2">Esqueceu de cronometrar? Lance aqui o tempo trabalhado.</span>
                  </div>
                ))}
                {carregandoHist ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status"></div>
                  </div>
                ) : histTarefas.length === 0 && histCliente.length === 0 ? (
                  <div className="alert alert-info mb-0">
                    <i className="fas fa-info-circle me-2"></i>
                    Nenhuma tarefa desta empresa tem tempo cronometrado ainda. Use o botão
                    ▶ na lista de tarefas para medir o tempo de execução.
                  </div>
                ) : (
                  histPorMes.map((g) => (
                    <div key={g.mes} className="mb-4">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="fw-bold mb-0" style={{ color: '#00205b' }}>
                          <i className="fas fa-calendar me-2"></i>
                          {g.mes.split('-').reverse().join('/')}
                        </h6>
                        <span className="badge bg-light text-dark border"
                          style={{ fontVariantNumeric: 'tabular-nums' }}
                          title="Tempo total cronometrado na competência">
                          <i className="fas fa-stopwatch me-1"></i>
                          {hms(g.totalSeg)}
                        </span>
                      </div>
                      <div className="table-responsive">
                        <table className="table table-sm table-striped align-middle mb-0">
                          <thead>
                            <tr>
                              <th style={{ width: '110px' }}>Data</th>
                              <th>Tarefa</th>
                              <th style={{ width: '110px' }} className="text-end">Tempo</th>
                              <th style={{ width: '180px' }}>Executada por</th>
                              <th style={{ width: '150px' }}>Concluída em</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.itens.map((t) => (
                              <tr key={t.id} style={t.tipo === 'cliente' ? { background: '#f4f8fc' } : undefined}>
                                <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{t.data}</td>
                                <td>
                                  {t.tipo === 'cliente' && (
                                    <i className="fas fa-building me-2" style={{ color: '#00b5cc' }}
                                      title="Tempo geral com a empresa aberta, fora de qualquer tarefa"></i>
                                  )}
                                  {t.tarefa}
                                </td>
                                <td className="text-end"
                                  style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                  {histEdit?.id === t.id ? (
                                    <span className="d-inline-flex align-items-center gap-1">
                                      <input type="text" className="form-control form-control-sm"
                                        style={{ width: '95px', fontVariantNumeric: 'tabular-nums' }}
                                        value={histEdit.valor} autoFocus
                                        placeholder="HH:MM:SS"
                                        onChange={(e) => setHistEdit(f => ({ ...f, valor: e.target.value }))}
                                        onKeyDown={(e) => { if (e.key === 'Enter') salvarTempo(); if (e.key === 'Escape') setHistEdit(null); }}
                                        disabled={salvando} />
                                      <button className="btn btn-sm btn-success p-1" onClick={salvarTempo}
                                        disabled={salvando} title="Salvar (Enter)">
                                        {salvando
                                          ? <span className="spinner-border spinner-border-sm"></span>
                                          : <i className="fas fa-check"></i>}
                                      </button>
                                      <button className="btn btn-sm btn-light p-1 border" onClick={() => setHistEdit(null)}
                                        disabled={salvando} title="Cancelar (Esc)">
                                        <i className="fas fa-times"></i>
                                      </button>
                                    </span>
                                  ) : (
                                    <>
                                      <span className="fw-bold" style={{ color: '#00205b' }}>{t.tempo_execucao}</span>
                                      {t.tipo !== 'cliente' && (
                                        <button className="btn btn-sm btn-light p-1 ms-2 border"
                                          onClick={() => setHistEdit({ id: t.id, valor: t.tempo_execucao || '' })}
                                          title="Editar o tempo desta tarefa">
                                          <i className="fas fa-pen text-muted" style={{ fontSize: '.72rem' }}></i>
                                        </button>
                                      )}
                                    </>
                                  )}
                                </td>
                                <td>{t.executor || t.responsavel || <span className="text-muted">-</span>}</td>
                                <td className="text-muted" style={{ fontSize: '.82rem', whiteSpace: 'nowrap' }}>
                                  {t.data_finalizada
                                    ? <>{t.data_finalizada}{t.hora_finalizada && ` ${t.hora_finalizada}`}</>
                                    : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary" onClick={() => setHistEmpresa(null)}>
                  <i className="fas fa-times me-2"></i>Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: EDITAR EMPRESA */}
      {empresaEdit && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: '640px' }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-edit me-2"></i>
                    Editar Empresa
                  </h5>
                  <small style={{ color: '#6c757d' }}>{empresaEdit.original} · {ROTULO_AREA[area]}</small>
                </div>
                <button type="button" className="btn-close" onClick={() => setEmpresaEdit(null)}
                  disabled={salvando}></button>
              </div>
              <div className="modal-body">
                <label className="form-label fw-bold">Nome da empresa</label>
                <input type="text" className="form-control" value={empresaEdit.nome}
                  onChange={(e) => setEmpresaEdit(f => ({ ...f, nome: e.target.value }))}
                  disabled={salvando} />
                <small className="text-muted">
                  Renomear atualiza tarefas, recorrências, anotações e acessos de uma vez.
                </small>

                <label className="form-label fw-bold mt-3">Operadora responsável</label>
                <select className="form-select" value={empresaEdit.operadora}
                  onChange={(e) => setEmpresaEdit(f => ({ ...f, operadora: e.target.value }))}
                  disabled={salvando}>
                  <option value="">Não alterar</option>
                  {responsaveis.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
                <small className="text-muted">
                  Aplicada às tarefas <strong>pendentes</strong> e aos modelos de recorrência —
                  as concluídas guardam quem realmente executou.
                </small>

                {/* ─── Dados cadastrais da empresa ─── */}
                <hr className="my-3" />
                <div className="fw-bold mb-2" style={{ color: '#00205b' }}>
                  <i className="fas fa-building me-2"></i>Dados da empresa
                  {empresaEdit.carregandoDados && (
                    <span className="spinner-border spinner-border-sm ms-2 text-muted"></span>
                  )}
                </div>
                {(() => {
                  const d = empresaEdit.dados || {};
                  const set = (k, v) => setEmpresaEdit(f => ({ ...f, dados: { ...f.dados, [k]: v } }));
                  return (
                    <div className="row g-2">
                      <div className="col-12">
                        <label className="form-label small fw-bold mb-1">Razão social</label>
                        <input type="text" className="form-control form-control-sm" value={d.razao_social || ''}
                          disabled={salvando} onChange={(e) => set('razao_social', e.target.value)} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small fw-bold mb-1">Nome fantasia</label>
                        <input type="text" className="form-control form-control-sm" value={d.nome_fantasia || ''}
                          disabled={salvando} onChange={(e) => set('nome_fantasia', e.target.value)} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small fw-bold mb-1">CNPJ</label>
                        <input type="text" className="form-control form-control-sm" placeholder="00.000.000/0000-00"
                          value={d.cnpj || ''} disabled={salvando} onChange={(e) => set('cnpj', e.target.value)} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small fw-bold mb-1">Regime tributário</label>
                        <select className="form-select form-select-sm" value={d.regime_tributario || ''}
                          disabled={salvando} onChange={(e) => set('regime_tributario', e.target.value)}>
                          <option value="">Selecione...</option>
                          {REGIMES.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small fw-bold mb-1">Inscr. estadual</label>
                        <input type="text" className="form-control form-control-sm" value={d.inscricao_estadual || ''}
                          disabled={salvando} onChange={(e) => set('inscricao_estadual', e.target.value)} />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small fw-bold mb-1">Inscr. municipal</label>
                        <input type="text" className="form-control form-control-sm" value={d.inscricao_municipal || ''}
                          disabled={salvando} onChange={(e) => set('inscricao_municipal', e.target.value)} />
                      </div>
                      <div className="col-12">
                        <label className="form-label small fw-bold mb-1">Endereço</label>
                        <input type="text" className="form-control form-control-sm" value={d.endereco || ''}
                          disabled={salvando} onChange={(e) => set('endereco', e.target.value)} />
                      </div>
                      <div className="col-12">
                        <label className="form-label small fw-bold mb-1">Observação</label>
                        <textarea className="form-control form-control-sm" rows={2} value={d.observacao || ''}
                          disabled={salvando} onChange={(e) => set('observacao', e.target.value)} />
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-outline-danger me-auto"
                  onClick={() => alternarAtivacao(empresaEdit.original, false)} disabled={salvando}
                  title="A empresa sai do painel e a recorrência para — nada é apagado">
                  <i className="fas fa-ban me-2"></i>Inativar empresa
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setEmpresaEdit(null)}
                  disabled={salvando}>
                  <i className="fas fa-times me-2"></i>Cancelar
                </button>
                <button type="button" className="btn btn-success" onClick={salvarEmpresa}
                  disabled={salvando || !empresaEdit.nome.trim()}>
                  {salvando
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Salvando...</>
                    : <><i className="fas fa-save me-2"></i>Salvar Alterações</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: VER DADOS DA EMPRESA (somente leitura) */}
      {dadosVer && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '560px' }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-building me-2 text-info"></i>Dados da empresa
                  </h5>
                  <small style={{ color: '#6c757d' }}>{dadosVer.empresa} · {ROTULO_AREA[area]}</small>
                </div>
                <button type="button" className="btn-close" onClick={() => setDadosVer(null)}></button>
              </div>
              <div className="modal-body">
                {dadosVer.carregando ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status"></div>
                  </div>
                ) : (() => {
                  const d = dadosVer.dados || {};
                  const linhas = [
                    ['Razão social', d.razao_social], ['Nome fantasia', d.nome_fantasia],
                    ['CNPJ', d.cnpj], ['Regime tributário', d.regime_tributario],
                    ['Inscrição estadual', d.inscricao_estadual],
                    ['Inscrição municipal', d.inscricao_municipal],
                    ['Endereço', d.endereco], ['Observação', d.observacao],
                  ];
                  const algum = linhas.some(([, v]) => v);
                  if (!algum) {
                    return (
                      <div className="alert alert-light border text-center mb-0">
                        <i className="fas fa-circle-info me-2 text-muted"></i>
                        Ainda não há dados cadastrais desta empresa. Use o botão
                        <i className="fas fa-edit mx-1"></i>(editar) para preencher.
                      </div>
                    );
                  }
                  return (
                    <table className="table table-sm mb-0">
                      <tbody>
                        {linhas.filter(([, v]) => v).map(([rot, val]) => (
                          <tr key={rot}>
                            <td className="text-muted" style={{ width: '40%', fontSize: '.85rem' }}>{rot}</td>
                            <td className="fw-semibold" style={{ fontSize: '.9rem' }}>{val}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                })()}
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-outline-secondary"
                  onClick={() => { const emp = dadosVer.empresa; setDadosVer(null); abrirEdicaoEmpresa(emp); }}>
                  <i className="fas fa-edit me-2"></i>Editar
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setDadosVer(null)}>
                  <i className="fas fa-times me-2"></i>Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: NOVA EMPRESA */}
      {novaEmpresa !== null && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-building me-2"></i>
                    Nova Empresa
                  </h5>
                  <small style={{ color: '#6c757d' }}>{ROTULO_AREA[area]} · atividades mensais</small>
                </div>
                <button type="button" className="btn-close" onClick={() => setNovaEmpresa(null)}></button>
              </div>
              <div className="modal-body">
                <label className="form-label fw-bold">
                  Nome da empresa <span className="text-danger">*</span>
                </label>
                <input type="text" className="form-control" autoFocus
                  placeholder="Ex: Órulo" value={novaEmpresa.nome}
                  onChange={(e) => setNovaEmpresa(f => ({ ...f, nome: e.target.value }))} />
                {empresasOpcoes.some(e => e.toLowerCase() === novaEmpresa.nome.trim().toLowerCase()) && (
                  <small className="text-danger">
                    <i className="fas fa-triangle-exclamation me-1"></i>
                    Essa empresa já existe no painel — abra ela pela lista.
                  </small>
                )}

                <label className="form-label fw-bold mt-3">
                  Operadora responsável <span className="text-danger">*</span>
                </label>
                <select className="form-select" value={novaEmpresa.operadora}
                  onChange={(e) => setNovaEmpresa(f => ({ ...f, operadora: e.target.value }))}>
                  <option value="">Selecione a operadora...</option>
                  {responsaveis.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
                <small className="text-muted">
                  As tarefas criadas para esta empresa já nascem atribuídas a ela.
                </small>
                <div className="alert alert-info mt-3 mb-0 small">
                  <i className="fas fa-info-circle me-2"></i>
                  A empresa passa a existir no painel <strong>assim que criada</strong> e você é
                  levado direto para ela para cadastrar as atividades.
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary" onClick={() => setNovaEmpresa(null)}>
                  <i className="fas fa-times me-2"></i>Cancelar
                </button>
                <button type="button" className="btn btn-success"
                  disabled={criandoEmpresa || !novaEmpresa.nome.trim() || !novaEmpresa.operadora
                    || empresasOpcoes.some(e => e.toLowerCase() === novaEmpresa.nome.trim().toLowerCase())}
                  onClick={criarEmpresa}>
                  <i className={`fas ${criandoEmpresa ? 'fa-spinner fa-spin' : 'fa-arrow-right'} me-2`}></i>
                  {criandoEmpresa ? 'Criando...' : 'Criar e abrir'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: ACESSOS DA EMPRESA (senhas salvas) */}
      {acessosEmpresa && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, overflowY: 'auto' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '1320px' }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-key me-2"></i>
                    Acessos — {acessosEmpresa}
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {carregandoAcessos ? 'Carregando...' : `${acessos.length} acesso(s)`} · {ROTULO_AREA[area]}
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setAcessosEmpresa(null)}
                  disabled={salvando}></button>
              </div>

              <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {/* formulário (Adicionar senha da PlayBPO) */}
                {acessoForm ? (
                  <div className="border rounded p-3 bg-light mb-3" style={{ borderLeft: '4px solid #00b5cc' }}>
                    <div className="fw-bold mb-3" style={{ color: '#212529' }}>
                      <i className={`fas ${acessoForm.id ? 'fa-edit' : 'fa-plus'} me-2 text-primary`}></i>
                      {acessoForm.id ? `Editando: ${acessoForm.nome || 'acesso'}` : 'Adicionar acesso'}
                    </div>
                    <div className="row g-2">
                      <div className="col-md-4">
                        <label className="form-label small fw-bold mb-1">
                          Nome <span className="text-danger">*</span>
                        </label>
                        <input type="text" className="form-control form-control-sm" value={acessoForm.nome}
                          onChange={setAc('nome')} disabled={salvando} placeholder="Ex: Banco Inter PJ" />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small fw-bold mb-1">
                          Tipo de acesso <span className="text-danger">*</span>
                        </label>
                        <select className="form-select form-select-sm" value={acessoForm.tipo}
                          onChange={setAc('tipo')} disabled={salvando}>
                          <option value="">Selecione...</option>
                          {TIPOS_ACESSO.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small fw-bold mb-1">URL / Link de acesso</label>
                        <input type="text" className="form-control form-control-sm" value={acessoForm.url}
                          onChange={setAc('url')} disabled={salvando} placeholder="https://..." />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small fw-bold mb-1">Usuário para acesso</label>
                        <input type="text" className="form-control form-control-sm" value={acessoForm.usuario}
                          onChange={setAc('usuario')} disabled={salvando} autoComplete="off" />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small fw-bold mb-1">Senha</label>
                        <div className="input-group input-group-sm">
                          <input type={mostrarSenhaForm ? 'text' : 'password'}
                            className="form-control form-control-sm" value={acessoForm.senha}
                            onChange={(e) => { setSenhaTocada(true); setAc('senha')(e); }}
                            placeholder={acessoForm.id && acessoForm.tem_senha ? 'em branco = mantém a atual' : ''}
                            disabled={salvando} autoComplete="new-password" />
                          {acessoForm.id && acessoForm.tem_senha && (
                            <button type="button" className="btn btn-outline-secondary btn-sm"
                              onClick={() => obterSenhaAcesso(acessoForm.id)
                                .then((v) => { setAcessoForm((f) => ({ ...f, senha: v })); setSenhaTocada(true); })
                                .catch(() => setMensagem('Não foi possível carregar a senha atual'))}
                              tabIndex={-1} title="Carregar a senha atual para editar">
                              <i className="fas fa-download"></i>
                            </button>
                          )}
                          <button type="button" className="btn btn-outline-secondary btn-sm"
                            onClick={() => setMostrarSenhaForm(v => !v)} tabIndex={-1}>
                            <i className={`fas ${mostrarSenhaForm ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                          </button>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small fw-bold mb-1">Operador</label>
                        <input type="text" className="form-control form-control-sm" value={acessoForm.operador}
                          onChange={setAc('operador')} disabled={salvando} />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small fw-bold mb-1">Código</label>
                        <input type="text" className="form-control form-control-sm" value={acessoForm.codigo}
                          onChange={setAc('codigo')} disabled={salvando} />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small fw-bold mb-1">Inscrição</label>
                        <input type="text" className="form-control form-control-sm" value={acessoForm.inscricao}
                          onChange={setAc('inscricao')} disabled={salvando} />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small fw-bold mb-1">Agência</label>
                        <input type="text" className="form-control form-control-sm" value={acessoForm.agencia}
                          onChange={setAc('agencia')} disabled={salvando} />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small fw-bold mb-1">Conta</label>
                        <input type="text" className="form-control form-control-sm" value={acessoForm.conta}
                          onChange={setAc('conta')} disabled={salvando} />
                      </div>
                      <div className="col-12">
                        <label className="form-label small fw-bold mb-1">Observações</label>
                        <textarea className="form-control form-control-sm" rows={3}
                          value={acessoForm.observacoes} onChange={setAc('observacoes')} disabled={salvando}
                          placeholder="Particularidades do acesso, tokens, procedimentos..." />
                      </div>
                      <div className="col-12 d-flex gap-2">
                        <button className="btn btn-sm btn-success" onClick={salvarAcesso} disabled={salvando}>
                          {salvando
                            ? <span className="spinner-border spinner-border-sm"></span>
                            : <><i className="fas fa-save me-2"></i>{acessoForm.id ? 'Salvar' : 'Confirmar'}</>}
                        </button>
                        <button className="btn btn-sm btn-secondary" onClick={() => setAcessoForm(null)}
                          disabled={salvando}>
                          Cancelar
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mb-3 d-flex align-items-center gap-2 flex-wrap">
                    <button className="btn btn-sm btn-primary"
                      onClick={() => { setAcessoForm({ ...ACESSO_VAZIO }); setMostrarSenhaForm(false); setSenhaTocada(false); }}>
                      <i className="fas fa-plus me-2"></i>
                      Adicionar acesso
                    </button>
                    <div className="input-group input-group-sm" style={{ maxWidth: 320 }}>
                      <span className="input-group-text"><i className="fas fa-magnifying-glass"></i></span>
                      <input type="text" className="form-control" placeholder="Buscar acesso (nome, tipo, usuário)"
                        aria-label="Buscar acesso" value={buscaAcesso} onChange={(e) => setBuscaAcesso(e.target.value)} />
                    </div>
                    <small className="text-muted">
                      <i className="fas fa-arrows-left-right me-1"></i>
                      Use as setas para mudar a ordem. A ordem vale para toda a equipe.
                    </small>
                  </div>
                )}

                {/* confirmação de exclusão */}
                {acessoExcluir && (
                  <div className="alert alert-warning d-flex align-items-center justify-content-between gap-3 flex-wrap">
                    <div>
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      Excluir o acesso <strong>{acessoExcluir.nome}</strong>? As credenciais guardadas
                      nele serão perdidas.
                    </div>
                    <div className="d-flex gap-2">
                      <button className="btn btn-sm btn-secondary" onClick={() => setAcessoExcluir(null)}
                        disabled={salvando}>Cancelar</button>
                      <button className="btn btn-sm btn-danger" onClick={excluirAcesso} disabled={salvando}>
                        {salvando
                          ? <span className="spinner-border spinner-border-sm"></span>
                          : <><i className="fas fa-trash me-2"></i>Excluir</>}
                      </button>
                    </div>
                  </div>
                )}

                {carregandoAcessos ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status"></div>
                  </div>
                ) : acessos.length === 0 ? (
                  <div className="alert alert-info mb-0">
                    <i className="fas fa-info-circle me-2"></i>
                    Nenhum acesso cadastrado para esta empresa. Use <strong>Adicionar acesso</strong> para
                    guardar bancos, drives e sistemas.
                  </div>
                ) : (
                  <div className="row g-3">
                    {acessosVisiveis.length === 0 && (
                      <div className="col-12 text-muted small">Nenhum acesso encontrado para "{buscaAcesso}".</div>
                    )}
                    {acessosVisiveis.map((a) => {
                      const pos = acessos.findIndex((x) => x.id === a.id);
                      const ordenavel = !buscaAcesso.trim();
                      const linhaCampo = (rotulo, valor, destaque) => (
                        <div key={rotulo} className="d-flex align-items-center gap-2 py-1"
                          style={{ fontSize: destaque ? '1rem' : '.9rem' }}>
                          <button className="btn btn-sm btn-light p-1 border-0"
                            onClick={() => copiar(valor, rotulo)} disabled={!valor}
                            title={valor ? `Copiar ${rotulo.toLowerCase()}` : 'Vazio'}>
                            <i className="fas fa-copy text-muted"></i>
                          </button>
                          <span className="text-muted text-uppercase fw-bold"
                            style={{ width: '78px', fontSize: '.7rem', letterSpacing: '.5px' }}>{rotulo}</span>
                          <span className={valor ? 'fw-semibold' : 'text-muted'}
                            style={{ wordBreak: 'break-all', color: valor ? '#212529' : undefined }}>{valor || '—'}</span>
                        </div>
                      );
                      return (
                        <div className="col-md-6 col-xxl-4" key={a.id}>
                          <div className="border rounded p-3 h-100 d-flex flex-column" style={{ background: '#fff' }}>
                            <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                              <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                                <span className="fw-bold" style={{ color: '#00205b', fontSize: '1.08rem' }}>{a.nome}</span>
                              </div>
                              <span className="badge bg-light text-dark border">{a.tipo}</span>
                            </div>

                            {/* login e senha primeiro: é o que se procura ao abrir o acesso */}
                            <div className="rounded px-2 py-1 mb-2" style={{ background: '#f2f6fb' }}>
                              {linhaCampo('Usuário', a.usuario, true)}
                              <div className="d-flex align-items-center gap-2 py-1" style={{ fontSize: '1rem' }}>
                                <button className="btn btn-sm btn-light p-1 border-0"
                                  onClick={() => obterSenhaAcesso(a.id)
                                    .then((v) => copiar(v, 'Senha'))
                                    .catch(() => setMensagem('Não foi possível obter a senha'))}
                                  disabled={!a.tem_senha}
                                  title={a.tem_senha ? 'Copiar senha' : 'Vazio'}>
                                  <i className="fas fa-copy text-muted"></i>
                                </button>
                                <span className="text-muted text-uppercase fw-bold"
                                  style={{ width: '78px', fontSize: '.7rem', letterSpacing: '.5px' }}>Senha</span>
                                <code style={{ wordBreak: 'break-all', fontSize: '1rem' }}>
                                  {a.tem_senha
                                    ? (senhasVisiveis.includes(a.id) ? (senhasReveladas[a.id] ?? '…') : '••••••••')
                                    : '—'}
                                </code>
                                {a.tem_senha && (
                                  <button className="btn btn-sm btn-light p-1 border-0 ms-auto"
                                    onClick={() => {
                                      if (senhasVisiveis.includes(a.id)) {
                                        setSenhasVisiveis(v => v.filter(x => x !== a.id));
                                      } else {
                                        obterSenhaAcesso(a.id)
                                          .then(() => setSenhasVisiveis(v => [...v, a.id]))
                                          .catch(() => setMensagem('Não foi possível obter a senha'));
                                      }
                                    }}
                                    title="Mostrar/ocultar senha">
                                    <i className={`fas ${senhasVisiveis.includes(a.id) ? 'fa-eye-slash' : 'fa-eye'} text-primary`}></i>
                                  </button>
                                )}
                              </div>
                            </div>

                            {a.url && (
                              <a href={/^https?:\/\//i.test(a.url) ? a.url : `https://${a.url}`}
                                target="_blank" rel="noreferrer"
                                className="btn btn-sm btn-success w-100 mb-2 fw-bold">
                                <i className="fas fa-arrow-up-right-from-square me-2"></i>
                                Acessar link
                              </a>
                            )}

                            {/* os demais campos só aparecem quando preenchidos */}
                            {[['Operador', a.operador], ['Código', a.codigo], ['Inscrição', a.inscricao],
                              ['Agência', a.agencia], ['Conta', a.conta]]
                              .filter(([, valor]) => valor)
                              .map(([rotulo, valor]) => linhaCampo(rotulo, valor, false))}

                            {a.observacoes && (
                              <div className="text-muted mt-1 fst-italic"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '.88rem' }}>
                                {a.observacoes}
                              </div>
                            )}

                            <div className="d-flex align-items-center gap-1 mt-auto pt-2">
                              {ordenavel && (
                                <>
                                  <button className="btn btn-sm btn-outline-secondary" disabled={pos === 0}
                                    onClick={() => moverAcesso(a.id, 'inicio')} title="Mover para o início">
                                    <i className="fas fa-angles-up"></i>
                                  </button>
                                  <button className="btn btn-sm btn-outline-secondary" disabled={pos === 0}
                                    onClick={() => moverAcesso(a.id, -1)} title="Mover uma posição para antes">
                                    <i className="fas fa-arrow-left"></i>
                                  </button>
                                  <button className="btn btn-sm btn-outline-secondary" disabled={pos === acessos.length - 1}
                                    onClick={() => moverAcesso(a.id, 1)} title="Mover uma posição para depois">
                                    <i className="fas fa-arrow-right"></i>
                                  </button>
                                </>
                              )}
                              <button className="btn btn-sm btn-outline-primary ms-auto"
                                onClick={() => { setAcessoExcluir(null); setMostrarSenhaForm(false); setSenhaTocada(false);
                                  setAcessoForm({ ...ACESSO_VAZIO, ...a }); }}
                                title="Editar">
                                <i className="fas fa-edit"></i>
                              </button>
                              <button className="btn btn-sm btn-outline-danger"
                                onClick={() => { setAcessoForm(null); setAcessoExcluir(a); }}
                                title="Excluir">
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="modal-footer bg-light">
                <small className="text-muted me-auto">
                  <i className="fas fa-lock me-1"></i>
                  Credenciais de acesso — não compartilhar fora do time responsável.
                </small>
                <button type="button" className="btn btn-secondary" onClick={() => setAcessosEmpresa(null)}
                  disabled={salvando}>
                  <i className="fas fa-times me-2"></i>Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: ANOTAÇÕES DA EMPRESA */}
      {mostrarAnotacoes && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, overflowY: 'auto' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '1000px' }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-note-sticky me-2"></i>
                    Anotações — {empresaSelecionada}
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {carregandoAnot ? 'Carregando...' : `${anotacoes.length} anotação(ões)`} · {ROTULO_AREA[area]}
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setMostrarAnotacoes(false)}
                  disabled={salvando}></button>
              </div>

              <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                {/* formulário de criação/edição */}
                {anotForm ? (
                  <div className="border rounded p-3 bg-light mb-3" style={{ borderLeft: '4px solid #00b5cc' }}>
                    <div className="fw-bold mb-2" style={{ color: '#212529' }}>
                      <i className={`fas ${anotForm.id ? 'fa-edit' : 'fa-plus'} me-2 text-primary`}></i>
                      {anotForm.id ? 'Editar anotação' : 'Nova anotação'}
                    </div>
                    <div className="mb-2">
                      <label className="form-label small fw-bold mb-1">Assunto</label>
                      <input type="text" className="form-control form-control-sm"
                        placeholder="Ex: Principais contatos" value={anotForm.assunto}
                        onChange={(e) => setAnotForm(f => ({ ...f, assunto: e.target.value }))}
                        disabled={salvando} />
                    </div>
                    <div className="mb-2">
                      <label className="form-label small fw-bold mb-1">Anotação</label>
                      <textarea className="form-control form-control-sm" rows={5}
                        placeholder="Conteúdo da anotação..." value={anotForm.anotacao}
                        onChange={(e) => setAnotForm(f => ({ ...f, anotacao: e.target.value }))}
                        disabled={salvando} />
                    </div>
                    <div className="d-flex gap-2">
                      <button className="btn btn-sm btn-success" onClick={salvarAnotacao} disabled={salvando}>
                        {salvando
                          ? <span className="spinner-border spinner-border-sm"></span>
                          : <><i className="fas fa-save me-2"></i>{anotForm.id ? 'Salvar' : 'Adicionar'}</>}
                      </button>
                      <button className="btn btn-sm btn-secondary" onClick={() => setAnotForm(null)} disabled={salvando}>
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mb-3">
                    <button className="btn btn-sm btn-primary"
                      onClick={() => setAnotForm({ assunto: '', anotacao: '' })}>
                      <i className="fas fa-plus me-2"></i>
                      Adicionar anotação
                    </button>
                  </div>
                )}

                {/* confirmação de exclusão */}
                {anotExcluir && (
                  <div className="alert alert-warning d-flex align-items-center justify-content-between gap-3 flex-wrap">
                    <div>
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      Excluir a anotação <strong>{anotExcluir.assunto}</strong>?
                    </div>
                    <div className="d-flex gap-2">
                      <button className="btn btn-sm btn-secondary" onClick={() => setAnotExcluir(null)}
                        disabled={salvando}>Cancelar</button>
                      <button className="btn btn-sm btn-danger" onClick={excluirAnotacao} disabled={salvando}>
                        {salvando
                          ? <span className="spinner-border spinner-border-sm"></span>
                          : <><i className="fas fa-trash me-2"></i>Excluir</>}
                      </button>
                    </div>
                  </div>
                )}

                {carregandoAnot ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status"></div>
                  </div>
                ) : anotacoes.length === 0 ? (
                  <div className="alert alert-info mb-0">
                    <i className="fas fa-info-circle me-2"></i>
                    Nenhuma anotação para esta empresa. Use <strong>Adicionar anotação</strong> para
                    registrar contatos, regras combinadas e particularidades.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped align-middle mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: '220px' }}>Assunto</th>
                          <th>Anotação</th>
                          <th style={{ width: '140px' }}>Atualizada</th>
                          <th className="text-center" style={{ width: '100px' }}>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {anotacoes.map((a) => (
                          <tr key={a.id}
                            style={anotForm?.id === a.id || anotExcluir?.id === a.id
                              ? { boxShadow: 'inset 3px 0 0 #00b5cc', background: '#eefbfd' }
                              : undefined}>
                            <td className="fw-bold">{a.assunto}</td>
                            <td style={{ whiteSpace: 'pre-wrap', fontSize: '.9rem' }}>{a.anotacao}</td>
                            <td className="text-muted" style={{ fontSize: '.82rem', whiteSpace: 'nowrap' }}>
                              {a.updated_at}
                            </td>
                            <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                              <button className="btn btn-sm btn-outline-primary me-1"
                                onClick={() => { setAnotExcluir(null); setAnotForm({ id: a.id, assunto: a.assunto, anotacao: a.anotacao }); }}
                                title="Editar">
                                <i className="fas fa-edit"></i>
                              </button>
                              <button className="btn btn-sm btn-outline-danger"
                                onClick={() => { setAnotForm(null); setAnotExcluir(a); }}
                                title="Excluir">
                                <i className="fas fa-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary" onClick={() => setMostrarAnotacoes(false)}
                  disabled={salvando}>
                  <i className="fas fa-times me-2"></i>Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: DESCRIÇÃO DA TAREFA */}
      {detalhe && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-align-left me-2"></i>
                    {detalhe.tarefa}
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {detalhe.empresa} · {detalhe.data}
                    {detalhe.responsavel && ` · ${detalhe.responsavel}`}
                    {seg(detalhe.tempo_execucao) > 0 && ` · tempo: ${detalhe.tempo_execucao}`}
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setDetalhe(null)} disabled={salvando}></button>
              </div>
              <div className="modal-body">
                <label className="form-label fw-bold">Descrição / como executar</label>
                <textarea className="form-control" rows={7} value={descricaoEdit}
                  onChange={(e) => setDescricaoEdit(e.target.value)} disabled={salvando}
                  placeholder="Passo a passo da tarefa, acessos necessários, observações..." />
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary" onClick={() => setDetalhe(null)} disabled={salvando}>
                  <i className="fas fa-times me-2"></i>Fechar
                </button>
                <button type="button" className="btn btn-success" onClick={salvarDescricao} disabled={salvando}>
                  {salvando
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Salvando...</>
                    : <><i className="fas fa-save me-2"></i>Salvar descrição</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: NOVA TAREFA / EDIÇÃO */}
      {form && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className={`fas ${form.id ? 'fa-edit' : 'fa-plus'} me-2`}></i>
                    {form.id ? 'Editar Tarefa' : 'Nova Tarefa'}
                  </h5>
                  <small style={{ color: '#6c757d' }}>{ROTULO_AREA[area]} · atividades mensais</small>
                </div>
                <button type="button" className="btn-close" onClick={() => setForm(null)} disabled={salvando}></button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label fw-bold">Data <span className="text-danger">*</span></label>
                    <input type="date" className="form-control" value={form.data}
                      onChange={setF('data')} disabled={salvando} />
                  </div>
                  <div className="col-md-8">
                    <label className="form-label fw-bold">Empresa <span className="text-danger">*</span></label>
                    <input type="text" className="form-control" list="bt-empresas" value={form.empresa}
                      onChange={setF('empresa')} disabled={salvando} placeholder="Nome da empresa" />
                    <datalist id="bt-empresas">
                      {empresasOpcoes.map(o => <option key={o} value={o} />)}
                    </datalist>
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-bold">Tarefa <span className="text-danger">*</span></label>
                    <input type="text" className="form-control" value={form.tarefa}
                      onChange={setF('tarefa')} disabled={salvando}
                      placeholder="Ex: Conciliação Bancária" />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-bold">Descrição</label>
                    <textarea className="form-control" rows={3} value={form.descricao}
                      onChange={setF('descricao')} disabled={salvando}
                      placeholder="Como executar, acessos, observações (opcional)" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Operadora responsável</label>
                    {/* o valor atual entra na lista mesmo se não estiver no cadastro
                        (tarefas importadas trazem nomes antigos da PlayBPO) */}
                    <select className="form-select" value={form.responsavel}
                      onChange={setF('responsavel')} disabled={salvando}>
                      <option value="">Sem operadora</option>
                      {[...new Set([
                        ...responsaveis,
                        ...(form.responsavel ? [form.responsavel] : []),
                      ])].sort((a, b) => a.localeCompare(b, 'pt-BR')).map(o => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                  {form.id ? (
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Status</label>
                      <select className="form-select" value={form.status} onChange={setF('status')} disabled={salvando}>
                        {Object.keys(CORES_STATUS).map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  ) : (
                    <div className="col-md-6 d-flex align-items-end">
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" id="bt-recorrente"
                          checked={form.recorrente} disabled={salvando}
                          onChange={(e) => setForm(f => ({
                            ...f, recorrente: e.target.checked,
                            // pré-preenche dia/mês a partir da data da tarefa
                            dia: f.dia || (f.data ? String(parseInt(f.data.split('-')[2], 10)) : ''),
                            mesAnual: f.mesAnual || (f.data ? String(parseInt(f.data.split('-')[1], 10)) : ''),
                          }))} />
                        <label className="form-check-label fw-bold" htmlFor="bt-recorrente">
                          Tarefa recorrente
                          <div className="small text-muted fw-normal">
                            nasce automaticamente conforme a periodicidade
                          </div>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* ─── PERIODICIDADE (estilo PlayBPO) ─── */}
                  {!form.id && form.recorrente && (
                    <div className="col-12">
                      <div className="border rounded p-3 bg-light">
                        <div className="fw-bold small text-muted text-uppercase mb-2">
                          Selecione a periodicidade
                        </div>
                        <div className="d-flex flex-wrap gap-3 mb-2">
                          {PERIODICIDADES.map(p => (
                            <div className="form-check" key={p}>
                              <input className="form-check-input" type="radio" name="bt-periodicidade"
                                id={`bt-per-${p}`} checked={form.periodicidade === p} disabled={salvando}
                                onChange={() => setForm(f => ({ ...f, periodicidade: p }))} />
                              <label className="form-check-label" htmlFor={`bt-per-${p}`}>{p}</label>
                            </div>
                          ))}
                        </div>

                        {form.periodicidade === 'Diária' && (
                          <small className="text-muted">
                            <i className="fas fa-info-circle me-1"></i>
                            Gerada em todos os <strong>dias úteis</strong> (segunda a sexta) de cada mês.
                          </small>
                        )}

                        {form.periodicidade === 'Semanal' && (
                          <div>
                            <label className="form-label small fw-bold mb-1">
                              Dias da semana <span className="text-danger">*</span>
                            </label>
                            <div className="d-flex flex-wrap gap-2">
                              {DIAS_SEMANA.map(([n, rotulo]) => (
                                <button key={n} type="button"
                                  className={`btn btn-sm ${form.diasSemana.includes(n) ? 'btn-primary' : 'btn-outline-secondary'}`}
                                  disabled={salvando}
                                  onClick={() => setForm(f => ({
                                    ...f,
                                    diasSemana: f.diasSemana.includes(n)
                                      ? f.diasSemana.filter(x => x !== n)
                                      : [...f.diasSemana, n].sort(),
                                  }))}>
                                  {rotulo}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {(form.periodicidade === 'Quinzenal' || form.periodicidade === 'Mensal') && (
                          <div className="row g-2">
                            <div className="col-md-3">
                              <label className="form-label small fw-bold mb-1">Dia do mês</label>
                              <input type="number" min={1} max={31} className="form-control form-control-sm"
                                value={form.dia} disabled={salvando}
                                onChange={(e) => setForm(f => ({ ...f, dia: e.target.value }))} />
                            </div>
                            <div className="col-md-9 d-flex align-items-end">
                              <small className="text-muted">
                                {form.periodicidade === 'Quinzenal'
                                  ? <>Gerada no dia informado e <strong>15 dias depois</strong> (ex.: dia 5 → 5 e 20).</>
                                  : <>Gerada todo mês neste dia — 29/30/31 caem no último dia do mês curto.</>}
                              </small>
                            </div>
                          </div>
                        )}

                        {form.periodicidade === 'Anual' && (
                          <div className="row g-2">
                            <div className="col-md-3">
                              <label className="form-label small fw-bold mb-1">Dia</label>
                              <input type="number" min={1} max={31} className="form-control form-control-sm"
                                value={form.dia} disabled={salvando}
                                onChange={(e) => setForm(f => ({ ...f, dia: e.target.value }))} />
                            </div>
                            <div className="col-md-4">
                              <label className="form-label small fw-bold mb-1">Mês</label>
                              <select className="form-select form-select-sm" value={form.mesAnual}
                                disabled={salvando}
                                onChange={(e) => setForm(f => ({ ...f, mesAnual: e.target.value }))}>
                                {MESES_ANO.map((nome, i) => (
                                  <option key={nome} value={i + 1}>{nome}</option>
                                ))}
                              </select>
                            </div>
                            <div className="col-md-5 d-flex align-items-end">
                              <small className="text-muted">Gerada uma vez por ano, nesta data.</small>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary" onClick={() => setForm(null)} disabled={salvando}>
                  <i className="fas fa-times me-2"></i>Cancelar
                </button>
                <button type="button" className="btn btn-success" onClick={salvar} disabled={salvando}>
                  {salvando
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Salvando...</>
                    : <><i className="fas fa-save me-2"></i>{form.id ? 'Salvar Alterações' : 'Criar Tarefa'}</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: CONFIRMAR EXCLUSÃO */}
      {excluir && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                  <i className="fas fa-exclamation-triangle me-2 text-danger"></i>
                  Confirmar Exclusão
                </h5>
                <button type="button" className="btn-close" onClick={() => setExcluir(null)} disabled={salvando}></button>
              </div>
              <div className="modal-body">
                <p className="mb-2">Excluir esta tarefa?</p>
                <div className="alert alert-warning mb-0">
                  <strong>{excluir.tarefa}</strong>
                  <div className="small mt-1">{excluir.empresa} · {excluir.data}</div>
                </div>
                {(excluir.origem === 'recorrencia' || excluir.origem === 'playbpo') && (
                  <div className="form-check mt-3">
                    <input className="form-check-input" type="checkbox" id="bt-parar-rec"
                      checked={pararRecorrencia}
                      onChange={(e) => setPararRecorrencia(e.target.checked)} />
                    <label className="form-check-label" htmlFor="bt-parar-rec">
                      <strong>Parar a recorrência</strong> — além de excluir esta,
                      a tarefa deixa de nascer nos próximos meses
                    </label>
                  </div>
                )}
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary" onClick={() => setExcluir(null)} disabled={salvando}>
                  <i className="fas fa-times me-2"></i>Cancelar
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmarExclusao} disabled={salvando}>
                  {salvando
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Excluindo...</>
                    : <><i className="fas fa-trash me-2"></i>Excluir</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BpoTarefasMensais;
